import ExcelStyles from "./excelStyles.js";

// Excel数据处理工具类
class ExcelData {
  // 清理数据以防止equivalentTo错误 - 修复数值转换问题
  static cleanDataForExcel(data) {
    try {
      // 使用JSON.parse(JSON.stringify())进行深度克隆和清理
      // 这将移除所有函数、undefined、循环引用等
      return JSON.parse(
        JSON.stringify(data, (key, value) => {
          // 特殊处理具有equivalentTo方法的对象
          if (
            typeof value === "object" &&
            value !== null &&
            typeof value.equivalentTo === "function"
          ) {
            // 尝试获取数值表示
            if (typeof value.valueOf === "function") {
              const numValue = value.valueOf();
              if (typeof numValue === "number") {
                return numValue;
              }
            }
            // 尝试转换为数值
            const numValue = Number(value.toString());
            if (!isNaN(numValue)) {
              return numValue;
            }
            // 如果无法转换为数值，返回字符串
            return value.toString();
          }
          // 跳过函数和undefined
          if (typeof value === "function" || value === undefined) {
            return undefined;
          }
          return value;
        }),
      );
    } catch (error) {
      console.error("JSON序列化清理数据失败:", error);
      // 如果JSON序列化失败，回退到递归清理
      return this.fallbackCleanData(data);
    }
  }

  // 回退清理方法
  static fallbackCleanData(data, visited) {
    // 如果visited未定义，则创建一个新的WeakMap
    if (visited === undefined) {
      visited = new WeakMap();
    }

    // 基本类型直接返回
    if (data === null || typeof data !== "object") {
      return data;
    }

    // 处理循环引用
    if (visited.has(data)) {
      return "[Circular Reference]";
    }
    visited.set(data, true);

    // 严格检查并处理具有equivalentTo方法的对象
    if (typeof data.equivalentTo === "function") {
      delete data.equivalentTo;
    }

    // 如果是数组，递归处理每一项
    if (Array.isArray(data)) {
      const result = [];
      for (const item of data) {
        result.push(this.fallbackCleanData(item, visited));
      }
      return result;
    }

    // 如果是Date对象，转换为字符串
    if (data instanceof Date) {
      return data.toISOString();
    }

    // 处理其他对象类型
    const cleaned = {};

    // 获取所有可枚举属性
    for (const [key, value] of Object.entries(data)) {
      // 跳过函数属性
      if (typeof value === "function") {
        continue;
      }

      // 递归清理属性值
      cleaned[key] = this.fallbackCleanData(value, visited);
    }

    return cleaned;
  }

  // 导出基础参数设置
  static exportGlobalParams(projectData, workbook) {
    const params = projectData["基础数据"] || {};

    // 创建工作表
    const worksheet = workbook.addWorksheet("基础参数设置");

    // 设置列宽，参数名称列20，参数值列15
    worksheet.columns = [{ width: 20 }, { width: 15 }];

    // 添加标题行 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "基础参数设置";

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    headerRow.values = ["参数名称", "参数值"];
    // 确保表头文字居中
    // headerRow.eachCell({ includeEmpty: true }, (cell) => {
    //   cell.alignment = {
    //     vertical: "middle",
    //     horizontal: "center",
    //   };
    // });
    // 表头样式将由ExcelStyles统一设置

    // 参数数据
    const paramsData = [
      ["项目名称", projectData.projectName || ""],
      ["建设投资", params.constructionInvestment || 0],
      ["计算期", Math.round(params.calculationPeriod || 0)],
      ["建设期", Math.round(params.constructionPeriod || 0)],
      ["运营期", Math.round(params.operationPeriod || 0)],
      ["正常年份", Math.round(params.normalYear || 0)],
      ["基准收益率", params.benchmarkYield || 0],
      ["折现率", params.discountRate || 0],
      ["增值税率", params.vatRate || 0],
      ["所得税率", params.incomeTaxRate || 0],
      ["行业基准收益率", params.industryBenchmarkYield || 0],
      ["贷款利率", params.loanRate || 0],
      ["残值率", params.salvageRate || 0],
      ["折旧年限", params.depreciationPeriod || 0],
      ["摊销年限", params.amortizationPeriod || 0],
      ["经营负荷", params.operatingLoad || 0],
    ];

    // 添加数据行
    paramsData.forEach((rowData, index) => {
      const row = worksheet.getRow(index + 3);
      row.values = rowData;
      row.height = 22;

      // 参数名称列 - 左对齐
      const nameCell = row.getCell(1);
      nameCell.font = {
        name: "Microsoft YaHei",
        size: 9, // 修改为9px
        bold: true,
        color: { argb: "FF333333" },
      };
      nameCell.alignment = {
        horizontal: "left", // 文字左对齐
        vertical: "middle",
      };
      nameCell.border = this.getDefaultBorder();

      // 参数值列 - 右对齐
      const valueCell = row.getCell(2);
      valueCell.font = {
        name: "Microsoft YaHei",
        size: 9, // 修改为9px
        bold: false,
        color: { argb: "FF333333" },
      };
      valueCell.alignment = {
        horizontal: "right", // 数值右对齐
        vertical: "middle",
      };
      valueCell.border = this.getDefaultBorder();

      // 设置数字格式
      if (typeof rowData[1] === "number") {
        if (rowData[1] >= 0 && rowData[1] <= 1) {
          valueCell.numFmt = "0.00%";
        } else {
          valueCell.numFmt = "#,##0.00";
        }
      }
    });

    // 应用Excel样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出总投资估算
  static exportInvestmentEstimate(projectData, workbook) {
    const investmentData = projectData["总投资估算"] || {};
    const totalTable = investmentData["总表"] || {};

    // 创建工作表
    const worksheet = workbook.addWorksheet("总投资估算");

    // 设置列宽
    worksheet.columns = [
      { header: "序号", width: 10 },
      { header: "工程或费用名称", width: 20 },
      { header: "建筑工程费", width: 15 },
      { header: "设备购置费", width: 15 },
      { header: "安装工程费", width: 15 },
      { header: "其他费用", width: 15 },
      { header: "合计", width: 15 },
      { header: "比例(%)", width: 15 },
    ];

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "总投资估算表";
    // 标题样式将由ExcelStyles统一设置

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    headerRow.values = [
      "序号",
      "工程或费用名称",
      "建筑工程费",
      "设备购置费",
      "安装工程费",
      "其他费用",
      "合计",
      "比例(%)",
    ];

    // 表头样式将由ExcelStyles统一设置

    // 添加数据行
    let currentRow = 3;

    // 一、建设投资
    const constructionInvestmentRow = worksheet.getRow(currentRow++);
    constructionInvestmentRow.values = [
      "一",
      "建设投资",
      (totalTable.建设投资?.建筑费用 || 0) !== 0
        ? Number((totalTable.建设投资?.建筑费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.设备费用 || 0) !== 0
        ? Number((totalTable.建设投资?.设备费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.安装费用 || 0) !== 0
        ? Number((totalTable.建设投资?.安装费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.其他费用 || 0) !== 0
        ? Number((totalTable.建设投资?.其他费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.合计 || 0) !== 0
        ? Number((totalTable.建设投资?.合计 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.比例 || 0) !== 0
        ? Number(((totalTable.建设投资?.比例 || 0) * 100).toFixed(2))
        : "" + " %",
    ];

    // 1、工程费用
    const projectCostsRow = worksheet.getRow(currentRow++);
    projectCostsRow.values = [
      "1",
      "工程费用",
      (totalTable.建设投资?.工程费用?.建筑费用 || 0) !== 0
        ? Number((totalTable.建设投资?.工程费用?.建筑费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.工程费用?.设备费用 || 0) !== 0
        ? Number((totalTable.建设投资?.工程费用?.设备费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.工程费用?.安装费用 || 0) !== 0
        ? Number((totalTable.建设投资?.工程费用?.安装费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.工程费用?.其他费用 || 0) !== 0
        ? Number((totalTable.建设投资?.工程费用?.其他费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.工程费用?.合计 || 0) !== 0
        ? Number((totalTable.建设投资?.工程费用?.合计 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.工程费用?.比例 || 0) !== 0
        ? Number(((totalTable.建设投资?.工程费用?.比例 || 0) * 100).toFixed(2))
        : "" + " %",
    ];

    // 2、工程建设其他费用
    const otherConstructionCostsRow = worksheet.getRow(currentRow++);
    otherConstructionCostsRow.values = [
      "2",
      "工程建设其他费用",
      (totalTable.建设投资?.工程建设其他费用?.建筑费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.工程建设其他费用?.建筑费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.工程建设其他费用?.设备费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.工程建设其他费用?.设备费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.工程建设其他费用?.安装费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.工程建设其他费用?.安装费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.工程建设其他费用?.其他费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.工程建设其他费用?.其他费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.工程建设其他费用?.合计 || 0) !== 0
        ? Number((totalTable.建设投资?.工程建设其他费用?.合计 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.工程建设其他费用?.比例 || 0) !== 0
        ? Number(
            ((totalTable.建设投资?.工程建设其他费用?.比例 || 0) * 100).toFixed(
              2,
            ),
          )
        : "",
    ];

    // 3、预备费
    const contingencyCostsRow = worksheet.getRow(currentRow++);
    contingencyCostsRow.values = [
      "3",
      "预备费",
      (totalTable.建设投资?.预备费?.建筑费用 || 0) !== 0
        ? Number((totalTable.建设投资?.预备费?.建筑费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.预备费?.设备费用 || 0) !== 0
        ? Number((totalTable.建设投资?.预备费?.设备费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.预备费?.安装费用 || 0) !== 0
        ? Number((totalTable.建设投资?.预备费?.安装费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.预备费?.其他费用 || 0) !== 0
        ? Number((totalTable.建设投资?.预备费?.其他费用 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.预备费?.合计 || 0) !== 0
        ? Number((totalTable.建设投资?.预备费?.合计 || 0).toFixed(2))
        : "",
      (totalTable.建设投资?.预备费?.比例 || 0) !== 0
        ? Number(((totalTable.建设投资?.预备费?.比例 || 0) * 100).toFixed(2))
        : "",
    ];

    // 3.1、基本预备费
    const basicContingencyCostsRow = worksheet.getRow(currentRow++);
    basicContingencyCostsRow.values = [
      "3.1",
      "基本预备费",
      (totalTable.建设投资?.预备费?.基本预备费?.建筑费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.预备费?.基本预备费?.建筑费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.预备费?.基本预备费?.设备费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.预备费?.基本预备费?.设备费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.预备费?.基本预备费?.安装费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.预备费?.基本预备费?.安装费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.预备费?.基本预备费?.其他费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.预备费?.基本预备费?.其他费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.预备费?.基本预备费?.合计 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.预备费?.基本预备费?.合计 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.预备费?.基本预备费?.比例 || 0) !== 0
        ? Number(
            (
              (totalTable.建设投资?.预备费?.基本预备费?.比例 || 0) * 100
            ).toFixed(2),
          )
        : "",
    ];

    // 3.2、涨价预备费
    const priceContingencyCostsRow = worksheet.getRow(currentRow++);
    priceContingencyCostsRow.values = [
      "3.2",
      "涨价预备费",
      (totalTable.建设投资?.预备费?.涨价预备费?.建筑费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.预备费?.涨价预备费?.建筑费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.预备费?.涨价预备费?.设备费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.预备费?.涨价预备费?.设备费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.预备费?.涨价预备费?.安装费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.预备费?.涨价预备费?.安装费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.预备费?.涨价预备费?.其他费用 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.预备费?.涨价预备费?.其他费用 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.预备费?.涨价预备费?.合计 || 0) !== 0
        ? Number(
            (totalTable.建设投资?.预备费?.涨价预备费?.合计 || 0).toFixed(2),
          )
        : "",
      (totalTable.建设投资?.预备费?.涨价预备费?.比例 || 0) !== 0
        ? Number(
            (
              (totalTable.建设投资?.预备费?.涨价预备费?.比例 || 0) * 100
            ).toFixed(2),
          )
        : "",
    ];

    // 二、建设期利息
    const constructionInterestRow = worksheet.getRow(currentRow++);
    constructionInterestRow.values = [
      "二",
      "建设期利息",
      (totalTable.建设期利息?.建筑费用 || 0) !== 0
        ? Number((totalTable.建设期利息?.建筑费用 || 0).toFixed(2))
        : "",
      (totalTable.建设期利息?.设备费用 || 0) !== 0
        ? Number((totalTable.建设期利息?.设备费用 || 0).toFixed(2))
        : "",
      (totalTable.建设期利息?.安装费用 || 0) !== 0
        ? Number((totalTable.建设期利息?.安装费用 || 0).toFixed(2))
        : "",
      (totalTable.建设期利息?.其他费用 || 0) !== 0
        ? Number((totalTable.建设期利息?.其他费用 || 0).toFixed(2))
        : "",
      (totalTable.建设期利息?.合计 || 0) !== 0
        ? Number((totalTable.建设期利息?.合计 || 0).toFixed(2))
        : "",
      (totalTable.建设期利息?.比例 || 0) !== 0
        ? Number(((totalTable.建设期利息?.比例 || 0) * 100).toFixed(2))
        : "",
    ];

    // 三、流动资金
    const workingCapitalRow = worksheet.getRow(currentRow++);
    workingCapitalRow.values = [
      "三",
      "流动资金",
      (totalTable.流动资金?.建筑费用 || 0) !== 0
        ? Number((totalTable.流动资金?.建筑费用 || 0).toFixed(2))
        : "",
      (totalTable.流动资金?.设备费用 || 0) !== 0
        ? Number((totalTable.流动资金?.设备费用 || 0).toFixed(2))
        : "",
      (totalTable.流动资金?.安装费用 || 0) !== 0
        ? Number((totalTable.流动资金?.安装费用 || 0).toFixed(2))
        : "",
      (totalTable.流动资金?.其他费用 || 0) !== 0
        ? Number((totalTable.流动资金?.其他费用 || 0).toFixed(2))
        : "",
      (totalTable.流动资金?.合计 || 0) !== 0
        ? Number((totalTable.流动资金?.合计 || 0).toFixed(2))
        : "",
      (totalTable.流动资金?.比例 || 0) !== 0
        ? Number(((totalTable.流动资金?.比例 || 0) * 100).toFixed(2))
        : "",
    ];

    // 四、总投资
    const totalInvestmentRow = worksheet.getRow(currentRow++);
    totalInvestmentRow.values = [
      "四",
      "总投资",
      (totalTable.总投资?.建筑费用 || 0) !== 0
        ? Number((totalTable.总投资?.建筑费用 || 0).toFixed(2))
        : "",
      (totalTable.总投资?.设备费用 || 0) !== 0
        ? Number((totalTable.总投资?.设备费用 || 0).toFixed(2))
        : "",
      (totalTable.总投资?.安装费用 || 0) !== 0
        ? Number((totalTable.总投资?.安装费用 || 0).toFixed(2))
        : "",
      (totalTable.总投资?.其他费用 || 0) !== 0
        ? Number((totalTable.总投资?.其他费用 || 0).toFixed(2))
        : "",
      (totalTable.总投资?.合计 || 0) !== 0
        ? Number((totalTable.总投资?.合计 || 0).toFixed(2))
        : "",
      (totalTable.总投资?.比例 || 0) !== 0
        ? Number(((totalTable.总投资?.比例 || 0) * 100).toFixed(2))
        : "",
    ];

    // 设置所有数据行样式
    for (let rowIndex = 3; rowIndex < currentRow; rowIndex++) {
      const row = worksheet.getRow(rowIndex);
      for (let colIndex = 1; colIndex <= 8; colIndex++) {
        const cell = row.getCell(colIndex);
        cell.border = this.getDefaultBorder();

        // 设置字体大小为9px
        cell.font = {
          name: "Microsoft YaHei",
          size: 9,
        };

        if (colIndex === 1) {
          cell.alignment = { vertical: "middle", horizontal: "center" }; // 序号列居中
        } else if (colIndex === 2) {
          cell.alignment = { vertical: "middle", horizontal: "left" }; // 项目名称列左对齐
        } else {
          cell.alignment = { vertical: "middle", horizontal: "right" }; // 数值列右对齐
        }
      }
    }

    // 应用Excel样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出建筑工程费用估算表
  static exportConstructionCost(projectData, workbook) {
    // 获取建筑工程费用数据
    const investmentData = projectData["总投资估算"] || {};
    const constructionCost = investmentData["建筑费用"] || {};

    // 确保tableData是一个数组
    const tableData = Array.isArray(constructionCost["tableData"])
      ? constructionCost["tableData"]
      : [];

    // 创建工作表
    const worksheet = workbook.addWorksheet("建筑工程费用估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 10 },
      { header: "名称", width: 20 },
      { header: "单位", width: 15 },
      { header: "数量", width: 15 },
      { header: "占地面积（㎡）", width: 15 },
      { header: "结构", width: 15 },
      { header: "单位造价（元）", width: 15 },
      { header: "金额（万元）", width: 15 },
    ];
    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "建筑工程费用估算表";

    // 创建表头
    const headerRow = worksheet.getRow(2);
    headerRow.values = [
      "序号",
      "名称",
      "单位",
      "数量",
      "占地面积（㎡）",
      "结构",
      "单位造价（元）",
      "金额（万元）",
    ];

    // 表头样式将由ExcelStyles统一设置

    // 添加数据行
    tableData.forEach((item, index) => {
      const row = worksheet.getRow(index + 3);
      row.values = [
        index + 1, // 序号
        item.name || "", // 名称
        item.unit || "", // 单位
        item.quantity || 0, // 数量
        item.area || 0, // 占地面积（㎡）
        item.structure || "", // 结构
        item.unitPrice || 0, // 单位造价（元）
        item.amount || 0, // 金额（万元）
      ];
    });

    // 添加合计行
    const totalRow = worksheet.getRow(tableData.length + 3);
    // 从数据中获取合计金额
    const totalAmount = constructionCost["合计"] || 0;
    totalRow.values = ["", "合计", "", "", "", "", "", totalAmount];

    // 设置合计行样式
    totalRow.eachCell({ includeEmpty: true }, (cell, index) => {
      cell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        horizontal: index < 2 ? "left" : "right",
        vertical: "middle",
      };
      cell.border = ExcelStyles.getDefaultBorder();
    });

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    // 返回工作表
    return worksheet;
  }
  // 导出设备工程费用估算表
  static exportEquipmentCost(projectData, workbook) {
    // 获取设备工程费用数据
    const investmentData = projectData["总投资估算"] || {};
    const equipmentCost = investmentData["设备工程"] || {};

    // 确保tableData是一个数组
    const tableData = Array.isArray(equipmentCost["tableData"])
      ? equipmentCost["tableData"]
      : [];

    // 创建工作表
    const worksheet = workbook.addWorksheet("设备工程费用估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 10 },
      { header: "名称", width: 20 },
      { header: "单位", width: 15 },
      { header: "数量", width: 15 },
      { header: "单价(元)", width: 15 },
      { header: "购置费(万元)", width: 15 },
      { header: "运费费率", width: 15 },
      { header: "运费(万元)", width: 15 },
      { header: "安装费率", width: 15 },
      { header: "安装费(万元)", width: 15 },
      { header: "合计金额(万元)", width: 15 },
    ];
    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "设备工程费用估算表";

    // 创建表头
    const headerRow = worksheet.getRow(2);
    headerRow.values = [
      "序号",
      "名称",
      "单位",
      "数量",
      "单价(元)",
      "购置费(万元)",
      "运费费率",
      "运费(万元)",
      "安装费率",
      "安装费(万元)",
      "合计金额(万元)",
    ];

    // 表头样式将由ExcelStyles统一设置

    // 添加数据行
    tableData.forEach((item, index) => {
      const row = worksheet.getRow(index + 3);

      // 获取基本数据
      const purchaseCost = item.purchaseCost || 0;

      // 计算或使用现有值
      const transportRate = item.transportRate || "1%"; // 默认运费费率1%
      const transportCost = item.transportCost || purchaseCost * 0.01; // 默认运费为购置费的1%
      const installRate = item.installRate || "1%"; // 默认安装费率1%
      const installCost = item.installCost || purchaseCost * 0.01; // 默认安装费为购置费的1%
      const totalAmount =
        item.totalAmount || purchaseCost + transportCost + installCost; // 计算合计金额

      row.values = [
        index + 1, // 序号
        item.name || "", // 名称
        item.unit || "", // 单位
        item.quantity || 0, // 数量
        item.unitPrice || 0, // 单价(元)
        purchaseCost, // 购置费(万元)
        transportRate, // 运费费率
        transportCost, // 运费(万元)
        installRate, // 安装费率
        installCost, // 安装费(万元)
        totalAmount, // 合计金额(万元)
      ];
    });

    // 添加合计行
    const totalRow = worksheet.getRow(tableData.length + 3);
    // 从数据中获取各合计金额
    const purchaseCostTotal = equipmentCost["购置费合计"] || 0;
    const transportCostTotal = equipmentCost["运输费合计"] || 0;
    const installCostTotal = equipmentCost["安装费合计"] || 0;
    const totalAmount = equipmentCost["合计"] || 0;

    totalRow.values = [
      "",
      "合计",
      "",
      "",
      "",
      purchaseCostTotal,
      "",
      transportCostTotal,
      "",
      installCostTotal,
      totalAmount,
    ];

    // 设置合计行样式
    totalRow.eachCell({ includeEmpty: true }, (cell, index) => {
      cell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        horizontal: index < 2 ? "left" : "right",
        vertical: "middle",
      };
      cell.border = this.getDefaultBorder();
    });

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出工程建设其他费用估算表
  static exportOtherCosts(projectData, workbook) {
    // 获取工程建设其他费用数据
    const investmentData = projectData["总投资估算"] || {};
    const otherCosts = investmentData["工程建设其他费用"] || {};

    // 获取无形资产和其他资产数据
    const intangibleAssets = Array.isArray(otherCosts["data"]?.["无形资产"])
      ? otherCosts["data"]["无形资产"]
      : [];
    const otherAssets = Array.isArray(otherCosts["data"]?.["其他资产"])
      ? otherCosts["data"]["其他资产"]
      : [];

    // 计算各部分小计
    const intangibleAssetsSubtotal = intangibleAssets.reduce(
      (sum, item) => sum + (item.amount || 0),
      0,
    );
    const otherAssetsSubtotal = otherAssets.reduce(
      (sum, item) => sum + (item.amount || 0),
      0,
    );
    const totalAmount =
      otherCosts["合计"] || intangibleAssetsSubtotal + otherAssetsSubtotal;

    // 创建工作表
    const worksheet = workbook.addWorksheet("工程建设其他费用估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 10 },
      { header: "费用名称", width: 20 },
      { header: "测算依据", width: 15 },
      { header: "金额（万元）", width: 15 },
    ];
    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "工程建设其他费用估算表";

    // 创建表头
    const headerRow = worksheet.getRow(2);
    headerRow.values = ["序号", "费用名称", "测算依据", "金额（万元）"];

    // 表头样式将由ExcelStyles统一设置

    // 初始化行索引
    let rowIndex = 3;
    let sequence = 1;

    // 添加无形资产部分（一级标题）
    const intangibleAssetsRow = worksheet.getRow(rowIndex);
    intangibleAssetsRow.values = [
      "一",
      "无形资产",
      "",
      intangibleAssetsSubtotal,
    ];
    rowIndex++;

    // 添加无形资产明细
    intangibleAssets.forEach((item) => {
      const row = worksheet.getRow(rowIndex);
      row.values = [
        sequence++,
        item.name || "",
        item.basis || "",
        item.amount || 0,
      ];
      rowIndex++;
    });

    // 添加其他资产部分（一级标题）
    const otherAssetsRow = worksheet.getRow(rowIndex);
    otherAssetsRow.values = ["二", "其他资产", "", otherAssetsSubtotal];
    rowIndex++;

    // 重置明细序号
    sequence = 1;

    // 添加其他资产明细
    otherAssets.forEach((item) => {
      const row = worksheet.getRow(rowIndex);
      row.values = [
        sequence++,
        item.name || "",
        item.basis || "",
        item.amount || 0,
      ];
      rowIndex++;
    });

    // 添加合计行
    const totalRow = worksheet.getRow(rowIndex);
    totalRow.values = ["", "合计", "", totalAmount];

    // 设置合计行样式
    totalRow.eachCell({ includeEmpty: true }, (cell, index) => {
      cell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        horizontal: index < 3 ? (index === 1 ? "left" : "center") : "right",
        vertical: "middle",
      };
      cell.border = ExcelStyles.getDefaultBorder();
    });

    // 设置样式（跳过表头样式，因为已经在上面设置了）
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出流动资金估算表
  static exportWorkingCapitalEstimate(projectData, workbook) {
    // 获取流动资金数据
    const investmentData = projectData["总投资估算"] || {};
    const workingCapital = investmentData["流动资金"] || {};

    // 确保tableData是一个数组
    const tableData = Array.isArray(workingCapital["tableData"])
      ? workingCapital["tableData"]
      : [];

    // 创建工作表
    const worksheet = workbook.addWorksheet("流动资金估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 10 },
      { header: "名称", width: 20 },
      { header: "单位", width: 15 },
      { header: "数量", width: 15 },
      { header: "单价(元)", width: 15 },
      { header: "金额(万元)", width: 15 },
      { header: "流动资金占用率", width: 15 },
      { header: "流动资金占用额(万元)", width: 15 },
    ];
    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "流动资金估算表";

    // 创建表头
    const headerRow = worksheet.getRow(2);
    headerRow.values = [
      "序号",
      "名称",
      "单位",
      "数量",
      "单价(元)",
      "金额(万元)",
      "流动资金占用率",
      "流动资金占用额(万元)",
    ];

    // 表头样式将由ExcelStyles统一设置

    // 添加数据行
    tableData.forEach((item, index) => {
      const row = worksheet.getRow(index + 3);
      row.values = [
        index + 1, // 序号
        item.name || "", // 名称
        item.unit || "", // 单位
        item.quantity || 0, // 数量
        item.unitPrice || 0, // 单价(元)
        item.amount || 0, // 金额(万元)
        item.workingCapitalRate || "", // 流动资金占用率
        item.workingCapitalAmount || 0, // 流动资金占用额(万元)
      ];
    });

    // 添加合计行
    const totalRow = worksheet.getRow(tableData.length + 3);
    // 从数据中获取各合计金额
    const amountTotal = workingCapital["金额合计"] || 0;
    const workingCapitalAmountTotal = workingCapital["流动资金占用额合计"] || 0;
    const totalAmount = workingCapital["合计"] || 0;

    totalRow.values = [
      "",
      "合计",
      "",
      "",
      "",
      amountTotal,
      "",
      workingCapitalAmountTotal,
      totalAmount,
    ];

    // 设置合计行样式
    totalRow.eachCell({ includeEmpty: true }, (cell, index) => {
      cell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        horizontal: index < 2 ? "left" : "right",
        vertical: "middle",
      };
      cell.border = this.getDefaultBorder();
    });

    // 设置样式（跳过表头样式，因为已经在上面设置了）
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }
  // 导出资金筹措表
  static exportFundingPlan(projectData, workbook) {
    // 创建工作表
    const worksheet = workbook.addWorksheet("资金筹措表");

    // 设置列宽
    worksheet.columns = [
      { header: "序号", width: 10 },
      { header: "资金来源类别", width: 25 },
      { header: "金额（万元）", width: 18 },
      { header: "占总投资比例（%）", width: 20 },
      { header: "筹措方法", width: 50 },
    ];

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "资金筹措表";

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    headerRow.values = [
      "序号",
      "资金来源类别",
      "金额（万元）",
      "占总投资比例（%）",
      "筹措方法",
    ];

    // 表头样式将由ExcelStyles统一设置

    // 获取资金筹措数据
    const fundingData =
      projectData["资金筹措及分年投资计划"]?.["资金筹措"] || {};

    // 数据行索引
    let rowIndex = 3;

    // 1. 项目资本金行
    const capitalRow = worksheet.getRow(rowIndex++);
    capitalRow.values = [
      "一",
      "项目资本金",
      fundingData.项目资本金?.amount || 0,
      fundingData.项目资本金?.proportion
        ? (fundingData.项目资本金.proportion * 100).toFixed(2) + "%"
        : "0.00%",
      "",
    ];

    // 2. 债务资金行
    const debtRow = worksheet.getRow(rowIndex++);
    debtRow.values = [
      "二",
      "债务资金",
      fundingData.债务资金?.amount || 0,
      fundingData.债务资金?.proportion
        ? (fundingData.债务资金.proportion * 100).toFixed(2) + "%"
        : "0.00%",
      "",
    ];

    // 3.1 银行借款行
    const bankLoan = fundingData.债务资金?.银行借款 || {};
    const bankLoanRow = worksheet.getRow(rowIndex++);
    bankLoanRow.values = [
      "1",
      "银行借款",
      bankLoan.amount || 0,
      bankLoan.proportion
        ? (bankLoan.proportion * 100).toFixed(2) + "%"
        : "0.00%",
      bankLoan.amount > 0
        ? `利率: ${bankLoan.rate || 0}% 借入期: 第${
            bankLoan.startyear
          }期 期限: ${bankLoan.term || 0}年 借入季度: 第${
            bankLoan.quarter
          }季度 还款方式: ${bankLoan.Repayment || ""}`
        : "",
    ];

    // 3.2 债券行
    const bond = fundingData.债务资金?.债券 || {};
    const bondRow = worksheet.getRow(rowIndex++);
    bondRow.values = [
      "2",
      "债券",
      bond.amount || 0,
      bond.proportion ? (bond.proportion * 100).toFixed(2) + "%" : "0.00%",
      bond.amount > 0
        ? `利率: ${bond.rate || 0}% 发行期: 第${bond.startyear}期 期限: ${
            bond.term || 0
          }年 发行季度: 第${bond.quarter}季度 还款方式: ${bond.Repayment || ""}`
        : "",
    ];

    // 3.3 融资租赁行
    const lease = fundingData.债务资金?.融资租赁 || {};
    const leaseRow = worksheet.getRow(rowIndex++);
    leaseRow.values = [
      "3",
      "融资租赁",
      lease.amount || 0,
      lease.proportion ? (lease.proportion * 100).toFixed(2) + "%" : "0.00%",
      lease.amount > 0
        ? `利率: ${lease.rate || 0}% 租入期: 第${lease.startyear}期 期限: ${
            lease.term || 0
          }年 租入季度: 第${lease.quarter}季度 还款方式: ${
            lease.Repayment || ""
          }`
        : "",
    ];

    // 3.4 其他借款行
    const otherLoan = fundingData.债务资金?.其他借款 || {};
    const otherLoanRow = worksheet.getRow(rowIndex++);
    otherLoanRow.values = [
      "4",
      "其他借款",
      otherLoan.amount || 0,
      otherLoan.proportion
        ? (otherLoan.proportion * 100).toFixed(2) + "%"
        : "0.00%",
      otherLoan.amount > 0
        ? `利率: ${otherLoan.rate || 0}% 借入期: 第${
            otherLoan.startyear
          }期 期限: ${otherLoan.term || 0}年 借入季度: 第${
            otherLoan.quarter
          }季度 还款方式: ${otherLoan.Repayment || ""}`
        : "",
    ];

    // 4. 总计行
    const totalRow = worksheet.getRow(rowIndex++);
    totalRow.values = [
      "",
      "资金筹措总额",
      fundingData.amount || 0,
      "100.00%",
      "",
    ];

    // 设置样式（跳过表头样式，因为已经在上面设置了）
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出分年投资计划
  static exportAnnualInvestmentPlan(projectData, workbook) {
    const constru = projectData["基础数据"] || {};
    const annualPlan =
      projectData["资金筹措及分年投资计划"]?.["分年投资计划"] || {};

    // 获取经营负荷100%的第一年 - 参考AnnualInvestmentPlan.vue
    const fullLoadYear =
      annualPlan.参数?.fullLoadYear || constru.calculationPeriod || 0;
    const constructionPeriod =
      annualPlan.参数?.constructionPeriod || constru.constructionPeriod || 0;
    const operatingPeriod = fullLoadYear - constructionPeriod;

    // 创建工作表
    const worksheet = workbook.addWorksheet("分年投资计划");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列 - 只显示到经营负荷100%的第一年
    const years = [];
    for (let year = 1; year <= fullLoadYear; year++) {
      columns.push({ header: year.toString(), width: 10 });
      years.push(year);
    }

    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    );
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "分年投资计划";

    // 创建表头第1行
    const headerRow1 = worksheet.getRow(2);
    const headerValues1 = ["序号", "项目", "合计"];
    for (let year = 1; year <= fullLoadYear; year++) {
      headerValues1.push(year);
    }
    headerRow1.values = headerValues1;

    // 表头样式将由ExcelStyles统一设置

    // 创建表头第2行 - 建设期/运营期
    const headerRow2 = worksheet.getRow(3);
    const headerValues2 = ["-", "建设期/运营期", "-"];

    // 暂时填充占位符，稍后进行跨列合并
    for (let year = 1; year <= fullLoadYear; year++) {
      headerValues2.push("");
    }

    headerRow2.values = headerValues2;
    for (let i = 1; i <= fullLoadYear + 3; i++) {
      const cell = headerRow2.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 建设期/运营期行样式将由ExcelStyles统一设置

    // 实现建设期和运营期的跨列居中
    if (constructionPeriod > 0) {
      // 建设期跨列合并 - 从第4列开始（A=1, B=2, C=3, D=4）
      const constructionStartCol = 4;
      const constructionEndCol = constructionStartCol + constructionPeriod - 1;
      worksheet.mergeCells(
        `D3:${String.fromCharCode(64 + constructionEndCol)}3`,
      );
      const constructionCell = worksheet.getCell(
        constructionStartCol === 4
          ? "D3"
          : `${String.fromCharCode(64 + constructionStartCol)}3`,
      );
      constructionCell.value = "建设期";

      // 设置合并单元格的样式
      constructionCell.border = this.getDefaultBorder();
      constructionCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      constructionCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      constructionCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    if (operatingPeriod > 0) {
      // 运营期跨列合并
      const operatingStartCol = 4 + constructionPeriod;
      const operatingEndCol = 3 + fullLoadYear; // 3是前3列（序号、项目、合计）
      worksheet.mergeCells(
        `${String.fromCharCode(64 + operatingStartCol)}3:${String.fromCharCode(
          64 + operatingEndCol,
        )}3`,
      );
      const operatingCell = worksheet.getCell(
        `${String.fromCharCode(64 + operatingStartCol)}3`,
      );
      operatingCell.value = "运营期";

      // 设置合并单元格的样式
      operatingCell.border = this.getDefaultBorder();
      operatingCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      operatingCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      operatingCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    // 设置表头行的背景色
    for (let col = 1; col <= fullLoadYear + 3; col++) {
      const cell = headerRow1.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      cell.border = this.getDefaultBorder();
    }

    // 添加数据行
    let currentRow = 4;

    // 辅助函数：获取年份数据，只取到fullLoadYear并格式化数值
    const getYearData = (yearDataArray) => {
      if (!Array.isArray(yearDataArray)) {
        return Array(fullLoadYear).fill("");
      }
      // 只取到fullLoadYear的数据，如果不够则用空字符串填充
      const result = [];
      for (let i = 0; i < fullLoadYear; i++) {
        const value = yearDataArray[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        result.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return result;
    };

    // 1. 总投资部分
    const totalInvestmentRow = worksheet.getRow(currentRow++);
    const totalInvestmentValues = [
      "1",
      "总投资",
      annualPlan.总投资?.amount || 0,
      ...getYearData(annualPlan.总投资?.year),
    ];
    totalInvestmentRow.values = totalInvestmentValues;

    // 1.1 建设投资
    const constructionRow = worksheet.getRow(currentRow++);
    const constructionValues = [
      "1.1",
      "建设投资",
      annualPlan.总投资?.建设投资?.amount || 0,
      ...getYearData(annualPlan.总投资?.建设投资?.year),
    ];
    constructionRow.values = constructionValues;

    // 1.2 建设期利息
    const interestRow = worksheet.getRow(currentRow++);
    const interestValues = [
      "1.2",
      "建设期利息",
      annualPlan.总投资?.建设期利息?.amount || 0,
      ...getYearData(annualPlan.总投资?.建设期利息?.year),
    ];
    interestRow.values = interestValues;

    // 1.3 流动资金
    const workingCapitalRow = worksheet.getRow(currentRow++);
    const workingCapitalValues = [
      "1.3",
      "流动资金",
      annualPlan.总投资?.流动资金?.amount || 0,
      ...getYearData(annualPlan.总投资?.流动资金?.year),
    ];
    workingCapitalRow.values = workingCapitalValues;

    // 2. 资金筹措部分
    const financingRow = worksheet.getRow(currentRow++);
    const financingValues = [
      "2",
      "资金筹措",
      annualPlan.资金筹措?.amount || 0,
      ...getYearData(annualPlan.资金筹措?.year),
    ];
    financingRow.values = financingValues;

    // 2.1 资本金（项目资本金）
    const capitalRow = worksheet.getRow(currentRow++);
    const capitalValues = [
      "2.1",
      "资本金（项目资本金）",
      annualPlan.资金筹措?.资本金?.amount || 0,
      ...getYearData(annualPlan.资金筹措?.资本金?.year),
    ];
    capitalRow.values = capitalValues;

    // 2.1.1 资本金明细 - 用于建设投资
    const capitalConstructionRow = worksheet.getRow(currentRow++);
    const capitalConstructionValues = [
      "2.1.1",
      "其中：用于建设投资",
      annualPlan.资金筹措?.资本金?.建设投资?.amount || 0,
      ...getYearData(annualPlan.资金筹措?.资本金?.建设投资?.year),
    ];
    capitalConstructionRow.values = capitalConstructionValues;

    // 2.1.2 资本金明细 - 建设期利息
    const capitalInterestRow = worksheet.getRow(currentRow++);
    const capitalInterestValues = [
      "2.1.2",
      "建设期利息",
      annualPlan.资金筹措?.资本金?.建设期利息?.amount || 0,
      ...getYearData(annualPlan.资金筹措?.资本金?.建设期利息?.year),
    ];
    capitalInterestRow.values = capitalInterestValues;

    // 2.1.3 资本金明细 - 用于流动资金
    const capitalWorkingRow = worksheet.getRow(currentRow++);
    const capitalWorkingValues = [
      "2.1.3",
      "用于流动资金",
      annualPlan.资金筹措?.资本金?.流动资金?.amount || 0,
      ...getYearData(annualPlan.资金筹措?.资本金?.流动资金?.year),
    ];
    capitalWorkingRow.values = capitalWorkingValues;

    // 2.2 债务资金（银行借款等）
    const debtRow = worksheet.getRow(currentRow++);
    const debtValues = [
      "2.2",
      "债务资金（银行借款等）",
      annualPlan.资金筹措?.债务资金?.amount || 0,
      ...getYearData(annualPlan.资金筹措?.债务资金?.year),
    ];
    debtRow.values = debtValues;

    // 2.2.1 用于建设投资
    const debtConstructionRow = worksheet.getRow(currentRow++);
    const debtConstructionValues = [
      "2.2.1",
      "用于建设投资",
      annualPlan.资金筹措?.债务资金?.建设投资?.amount || 0,
      ...getYearData(annualPlan.资金筹措?.债务资金?.建设投资?.year),
    ];
    debtConstructionRow.values = debtConstructionValues;

    // 2.2.2 用于流动资金
    const debtWorkingRow = worksheet.getRow(currentRow++);
    const debtWorkingValues = [
      "2.2.2",
      "用于流动资金",
      annualPlan.资金筹措?.债务资金?.流动资金?.amount || 0,
      ...getYearData(annualPlan.资金筹措?.债务资金?.流动资金?.year),
    ];
    debtWorkingRow.values = debtWorkingValues;

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出还本付息
  static exportRepayment(projectData, workbook) {
    const repaymentData =
      projectData["资金筹措及分年投资计划"]?.["还本付息"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 0;
    const constructionPeriod = constru.constructionPeriod || 0;

    // 获取正确的数据结构
    const repaymentArray = repaymentData.repaymentData || [];
    const addedFunds = repaymentData.addedFunds || {};

    // 创建工作表
    const worksheet = workbook.addWorksheet("还本付息");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const lastCol = String.fromCharCode(65 + 2 + calculationPeriod); // A=65, 序号+项目+合计+年份列数
    worksheet.mergeCells(`A1:${lastCol}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "还本付息计划表";

    // 创建表头 - 年份行
    const yearHeaderRow = worksheet.getRow(2);
    const yearHeaderValues = ["序号", "项目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      yearHeaderValues.push(year);
    }
    yearHeaderRow.values = yearHeaderValues;

    // 创建表头 - 建设期/运营期行
    const periodHeaderRow = worksheet.getRow(3);
    const periodHeaderValues = ["-", "建设期/运营期", "-"];

    // 添加建设期/运营期标识
    for (let year = 1; year <= calculationPeriod; year++) {
      if (year <= constructionPeriod) {
        periodHeaderValues.push("建设期");
      } else {
        periodHeaderValues.push("运营期");
      }
    }
    periodHeaderRow.values = periodHeaderValues;
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodHeaderRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 合并建设期/运营期单元格
    if (constructionPeriod > 0) {
      const constructionStartCol = 4; // 序号(1) + 项目(2) + 合计(3) + 1
      const constructionEndCol = constructionStartCol + constructionPeriod - 1;
      const constructionStartColChar = String.fromCharCode(
        65 + constructionStartCol - 1,
      );
      const constructionEndColChar = String.fromCharCode(
        65 + constructionEndCol - 1,
      );
      worksheet.mergeCells(
        `${constructionStartColChar}3:${constructionEndColChar}3`,
      );
      // 设置建设期单元格值
      const constructionCell = worksheet.getCell(
        `${constructionStartColChar}3`,
      );
      constructionCell.value = "建设期";
      // 设置合并单元格的样式
      constructionCell.border = this.getDefaultBorder();
      constructionCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      constructionCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      constructionCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    if (calculationPeriod > constructionPeriod) {
      const operationStartCol = 4 + constructionPeriod;
      const operationEndCol = 3 + calculationPeriod;
      const operationStartColChar = String.fromCharCode(
        65 + operationStartCol - 1,
      );
      const operationEndColChar = String.fromCharCode(65 + operationEndCol - 1);
      worksheet.mergeCells(`${operationStartColChar}3:${operationEndColChar}3`);
      // 设置运营期单元格值
      const operationCell = worksheet.getCell(`${operationStartColChar}3`);
      operationCell.value = "运营期";
      // 设置合并单元格的样式
      operationCell.border = this.getDefaultBorder();
      operationCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      operationCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      operationCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 辅助函数：获取年份数据，确保长度正确并格式化数值
    const getYearData = (data, key, period) => {
      const yearData = data[key] || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        const value = yearData[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        paddedData.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return paddedData;
    };

    // 循环遍历所有借款类型
    repaymentArray.forEach((loanType) => {
      // 借款类型主行
      const loanTypeRow = worksheet.getRow(currentRow++);
      const loanTypeYearData = getYearData(
        loanType,
        "借款本金",
        calculationPeriod,
      );
      const loanTypeValues = [
        seq++,
        loanType.名称,
        loanType["借款本金total"] || 0,
        ...loanTypeYearData,
      ];
      loanTypeRow.values = loanTypeValues;

      // 期初本金
      const principalStartRow = worksheet.getRow(currentRow++);
      const principalStartYearData = getYearData(
        loanType,
        "期初本金",
        calculationPeriod,
      );
      const principalStartValues = [
        "",
        "期初本金",
        "",
        ...principalStartYearData,
      ];
      principalStartRow.values = principalStartValues;

      // 本年付息
      const interestRow = worksheet.getRow(currentRow++);
      const interestYearData = getYearData(
        loanType,
        "当期还息",
        calculationPeriod,
      );
      const interestValues = [
        "",
        "本年付息",
        loanType["当期还息total"] || 0,
        ...interestYearData,
      ];
      interestRow.values = interestValues;

      // 本年还本
      const principalRow = worksheet.getRow(currentRow++);
      const principalYearData = getYearData(
        loanType,
        "当期还本",
        calculationPeriod,
      );
      const principalValues = [
        "",
        "本年还本",
        loanType["当期还本total"] || 0,
        ...principalYearData,
      ];
      principalRow.values = principalValues;

      // 期末本金
      const principalEndRow = worksheet.getRow(currentRow++);
      const principalEndYearData = getYearData(
        loanType,
        "期末本金",
        calculationPeriod,
      );
      const principalEndValues = [
        "",
        "期末本金",
        loanType["期末本金total"] || 0,
        ...principalEndYearData,
      ];
      principalEndRow.values = principalEndValues;
    });

    // 借款偿还主行
    const repaymentMainRow = worksheet.getRow(currentRow++);
    const repaymentMainValues = [
      seq++,
      "借款偿还",
      "",
      ...Array(calculationPeriod).fill(0),
    ];
    repaymentMainRow.values = repaymentMainValues;

    // 期初可用还款资金
    const availableFundsRow = worksheet.getRow(currentRow++);
    const availableFundsYearData = getYearData(
      addedFunds,
      "期初可用还款资金",
      calculationPeriod,
    );
    const availableFundsValues = [
      "",
      "期初可用还款资金",
      "",
      ...availableFundsYearData,
    ];
    availableFundsRow.values = availableFundsValues;

    // 本年新增还款资金
    const newFundsRow = worksheet.getRow(currentRow++);
    const newFundsYearData = getYearData(
      addedFunds,
      "本年新增还款资金",
      calculationPeriod,
    );
    let newFundsTotal = 0;
    for (let i = 0; i < newFundsYearData.length; i++) {
      newFundsTotal += newFundsYearData[i] || 0;
    }
    const newFundsValues = [
      "",
      "本年新增还款资金",
      newFundsTotal,
      ...newFundsYearData,
    ];
    newFundsRow.values = newFundsValues;

    // 当期本金还款
    const principalRepaymentRow = worksheet.getRow(currentRow++);
    const principalRepaymentYearData = getYearData(
      addedFunds,
      "当期本金还款",
      calculationPeriod,
    );
    let principalRepaymentTotal = 0;

    for (let i = 0; i < principalRepaymentYearData.length; i++) {
      principalRepaymentTotal += principalRepaymentYearData[i] || 0;
    }

    const principalRepaymentValues = [
      "",
      "当期本金还款",
      principalRepaymentTotal,
      ...principalRepaymentYearData,
    ];
    principalRepaymentRow.values = principalRepaymentValues;

    // 当期利息还款
    const interestRepaymentRow = worksheet.getRow(currentRow++);
    const interestRepaymentYearData = getYearData(
      addedFunds,
      "当期利息还款",
      calculationPeriod,
    );
    let interestRepaymentTotal = 0;
    for (let i = 0; i < interestRepaymentYearData.length; i++) {
      interestRepaymentTotal += interestRepaymentYearData[i] || 0;
    }

    const interestRepaymentValues = [
      "",
      "当期利息还款",
      interestRepaymentTotal,
      ...interestRepaymentYearData,
    ];
    interestRepaymentRow.values = interestRepaymentValues;

    // 期末可用资金
    const endFundsRow = worksheet.getRow(currentRow++);
    const endFundsYearData = getYearData(
      addedFunds,
      "期末可用资金",

      calculationPeriod,
    );

    let kyentTotal = endFundsYearData[endFundsYearData.length - 1];

    const endFundsValues = [
      "",
      "期末可用资金",
      kyentTotal,
      ...endFundsYearData,
    ];
    endFundsRow.values = endFundsValues;

    // 利息备付率
    const interestCoverageRow = worksheet.getRow(currentRow++);
    const interestCoverageYearData = getYearData(
      addedFunds,
      "利息备付率",
      calculationPeriod,
    );
    let interestCoverageTotal = 0;
    for (let i = 0; i < interestCoverageYearData.length; i++) {
      if (interestCoverageYearData[i]) {
        interestCoverageTotal = interestCoverageYearData[i];
      }
    }
    const interestCoverageValues = [
      "",
      "利息备付率",
      interestCoverageTotal,
      ...interestCoverageYearData,
    ];
    interestCoverageRow.values = interestCoverageValues;

    // 设置利息备付率行样式
    interestCoverageRow.eachCell({ includeEmpty: true }, (cell, colIndex) => {
      if (colIndex > 0) {
        // 跳过空的第一个单元格
        // 先设置通用样式
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE3F2FD" }, // 与表头相同的背景色
        };

        // 设置字体样式（确保加粗）
        cell.font = {
          name: "Microsoft YaHei",
          size: 9,
          bold: true, // 加粗显示
          color: { argb: "FF34495E" },
        };

        // 根据列索引设置不同的对齐方式
        if (colIndex === 2) {
          // 第二列为项目名称，左对齐
          cell.alignment = {
            horizontal: "left", // 项目名称左对齐
            vertical: "middle",
          };
        } else {
          // 其他数值列右对齐
          cell.alignment = {
            horizontal: "right", // 数值右对齐
            vertical: "middle",
          };
        }

        // 设置边框
        cell.border = this.getDefaultBorder();
      }
    });

    // 本金备付率
    const principalCoverageRow = worksheet.getRow(currentRow++);
    const principalCoverageYearData = getYearData(
      addedFunds,
      "本金备付率",
      calculationPeriod,
    );
    let principalCoverageTotal = 0;
    for (let i = 0; i < principalCoverageYearData.length; i++) {
      if (principalCoverageYearData[i]) {
        principalCoverageTotal = principalCoverageYearData[i];
      }
    }
    const principalCoverageValues = [
      "",
      "本金备付率",
      principalCoverageTotal,
      ...principalCoverageYearData,
    ];
    principalCoverageRow.values = principalCoverageValues;

    // 设置本金备付率行样式
    principalCoverageRow.eachCell({ includeEmpty: true }, (cell, colIndex) => {
      if (colIndex > 0) {
        // 跳过空的第一个单元格
        // 先设置通用样式
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE3F2FD" }, // 与表头相同的背景色
        };

        // 设置字体样式（确保加粗）
        cell.font = {
          name: "Microsoft YaHei",
          size: 9,
          bold: true, // 加粗显示
          color: { argb: "FF34495E" },
        };

        // 根据列索引设置不同的对齐方式
        if (colIndex === 2) {
          // 第二列为项目名称，左对齐
          cell.alignment = {
            horizontal: "left", // 项目名称左对齐
            vertical: "middle",
          };
        } else {
          // 其他数值列右对齐
          cell.alignment = {
            horizontal: "right", // 数值右对齐
            vertical: "middle",
          };
        }

        // 设置边框
        cell.border = this.getDefaultBorder();
      }
    });

    // 本息备付率
    const principalInterestCoverageRow = worksheet.getRow(currentRow++);
    const principalInterestCoverageYearData = getYearData(
      addedFunds,
      "本息备付率",
      calculationPeriod,
    );
    let principalInterestCoverageTotal = 0;
    for (let i = 0; i < principalInterestCoverageYearData.length; i++) {
      if (principalInterestCoverageYearData[i]) {
        principalInterestCoverageTotal = principalInterestCoverageYearData[i];
      }
    }
    const principalInterestCoverageValues = [
      "",
      "本息备付率",
      principalInterestCoverageTotal,
      ...principalInterestCoverageYearData,
    ];
    principalInterestCoverageRow.values = principalInterestCoverageValues;

    // 设置本息备付率行样式
    principalInterestCoverageRow.eachCell(
      { includeEmpty: true },
      (cell, colIndex) => {
        if (colIndex > 0) {
          // 跳过空的第一个单元格
          // 先设置通用样式
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFE3F2FD" }, // 与表头相同的背景色
          };

          // 设置字体样式（确保加粗）
          cell.font = {
            name: "Microsoft YaHei",
            size: 9,
            bold: true, // 加粗显示
            color: { argb: "FF34495E" },
          };

          // 根据列索引设置不同的对齐方式
          if (colIndex === 2) {
            // 第二列为项目名称，左对齐
            cell.alignment = {
              horizontal: "left", // 项目名称左对齐
              vertical: "middle",
            };
          } else {
            // 其他数值列右对齐
            cell.alignment = {
              horizontal: "right", // 数值右对齐
              vertical: "middle",
            };
          }

          // 设置边框
          cell.border = this.getDefaultBorder();
        }
      },
    );

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }
  // 导出总成本费用估算表
  static exportTotalCost(projectData, workbook) {
    const costData = projectData["成本费用"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 15;
    const constructionPeriod = constru.constructionPeriod || 1;

    // 创建工作表
    const worksheet = workbook.addWorksheet("总成本费用估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const lastCol = String.fromCharCode(65 + 2 + calculationPeriod); // A=65, 序号+项目+合计+年份列数
    worksheet.mergeCells(`A1:${lastCol}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "总成本费用估算表";

    // 创建表头 - 年份行
    const yearHeaderRow = worksheet.getRow(2);
    const yearHeaderValues = ["序号", "项目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      yearHeaderValues.push(year);
    }
    yearHeaderRow.values = yearHeaderValues;

    // 创建表头 - 建设期/运营期行
    const periodHeaderRow = worksheet.getRow(3);
    const periodHeaderValues = ["-", "建设期/运营期", "-"];

    // 添加建设期/运营期标识
    for (let year = 1; year <= calculationPeriod; year++) {
      if (year <= constructionPeriod) {
        periodHeaderValues.push("建设期");
      } else {
        periodHeaderValues.push("运营期");
      }
    }
    periodHeaderRow.values = periodHeaderValues;
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodHeaderRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 合并建设期/运营期单元格
    if (constructionPeriod > 0) {
      const constructionStartCol = 4; // 序号(1) + 项目(2) + 合计(3) + 1
      const constructionEndCol = constructionStartCol + constructionPeriod - 1;
      const constructionStartColChar = String.fromCharCode(
        65 + constructionStartCol - 1,
      );
      const constructionEndColChar = String.fromCharCode(
        65 + constructionEndCol - 1,
      );
      worksheet.mergeCells(
        `${constructionStartColChar}3:${constructionEndColChar}3`,
      );
      // 设置建设期单元格值
      const constructionCell = worksheet.getCell(
        `${constructionStartColChar}3`,
      );
      constructionCell.value = "建设期";
      // 设置合并单元格的样式
      constructionCell.border = this.getDefaultBorder();
      constructionCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      constructionCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      constructionCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    if (calculationPeriod > constructionPeriod) {
      const operationStartCol = 4 + constructionPeriod;
      const operationEndCol = 3 + calculationPeriod;
      const operationStartColChar = String.fromCharCode(
        65 + operationStartCol - 1,
      );
      const operationEndColChar = String.fromCharCode(65 + operationEndCol - 1);
      worksheet.mergeCells(`${operationStartColChar}3:${operationEndColChar}3`);
      // 设置运营期单元格值
      const operationCell = worksheet.getCell(`${operationStartColChar}3`);
      operationCell.value = "运营期";
      // 设置合并单元格的样式
      operationCell.border = this.getDefaultBorder();
      operationCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      operationCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      operationCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    // 辅助函数：获取年份数据，确保长度正确并格式化数值
    const getYearData = (data, period) => {
      const yearData = data?.year || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        let value = yearData[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        paddedData.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return paddedData;
    };

    // 辅助函数：格式化数值数组，保留两位小数，零值显示为空
    const bl2 = (data) => {
      let s = [];
      (data || []).forEach((item) => {
        // 确保item是数字类型
        const numValue = typeof item === "number" ? item : Number(item) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        s.push(Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)));
      });
      return s;
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 原辅材料
    const rawmaterialRow = worksheet.getRow(currentRow++);
    const rawmaterialData = costData["原辅材料"] || { total: 0, year: [] };
    const rawmaterialYearData = bl2(rawmaterialData.total?.year || []);
    const rawmaterialTotal = rawmaterialData.total?.total || 0;
    // 确保rawmaterialTotal是数字类型
    const safeRawmaterialTotal =
      typeof rawmaterialTotal === "number"
        ? rawmaterialTotal
        : Number(rawmaterialTotal) || 0;
    const rawmaterialValues = [
      seq++,
      "原辅材料",
      safeRawmaterialTotal !== 0 ? Number(safeRawmaterialTotal).toFixed(2) : "",
      ...rawmaterialYearData,
    ];
    rawmaterialRow.values = rawmaterialValues;

    // 燃料动力
    const fuelPowerRow = worksheet.getRow(currentRow++);
    const fuelPowerData = costData["燃料动力"] || {};
    const fuelPowerYearData = bl2(fuelPowerData.total?.year || []);
    const fuelPowerTotal = fuelPowerData.total.total || 0;
    const fuelPowerValues = [
      seq++,
      "燃料动力",
      fuelPowerTotal !== 0 ? Number(fuelPowerTotal).toFixed(2) : "",
      ...fuelPowerYearData,
    ];
    fuelPowerRow.values = fuelPowerValues;

    // 工资福利
    const wagesBenefitsRow = worksheet.getRow(currentRow++);
    const wagesBenefitsData = costData["职工薪酬"] || {};
    const wagesBenefitsYearData = bl2(wagesBenefitsData.total?.year || []);
    const wagesBenefitsTotal = wagesBenefitsData.total?.total || 0;
    const wagesBenefitsValues = [
      seq++,
      "工资福利",
      wagesBenefitsTotal !== 0 ? Number(wagesBenefitsTotal).toFixed(2) : "",
      ...wagesBenefitsYearData,
    ];
    wagesBenefitsRow.values = wagesBenefitsValues;

    // 修理费
    const repairRow = worksheet.getRow(currentRow++);
    const repairData = costData["修理费"] || { total: 0, year: [] };
    const repairYearData = getYearData(repairData, calculationPeriod);
    const repairTotal = repairData.total || 0;
    const repairValues = [
      seq++,
      "修理费",
      repairTotal !== 0 ? Number(repairTotal.toFixed(2)) : "",
      ...repairYearData,
    ];
    repairRow.values = repairValues;

    // 其他费用
    const otherRow = worksheet.getRow(currentRow++);
    const otherData = costData["其他费用"] || { total: 0, year: [] };
    const otherYearData = getYearData(otherData, calculationPeriod);
    const otherTotal = otherData.total || 0;
    const otherValues = [
      seq++,
      "其他费用",
      otherTotal !== 0 ? Number(otherTotal.toFixed(2)) : "",
      ...otherYearData,
    ];
    otherRow.values = otherValues;

    // 经营成本
    const operatingCostsRow = worksheet.getRow(currentRow++);
    const operatingCostsData = costData["经营成本"] || { total: 0, year: [] };
    const operatingCostsYearData = getYearData(
      operatingCostsData,
      calculationPeriod,
    );
    const operatingCostsTotal = operatingCostsData.total || 0;
    const operatingCostsValues = [
      "",
      "经营成本",
      operatingCostsTotal !== 0 ? Number(operatingCostsTotal.toFixed(2)) : "",
      ...operatingCostsYearData,
    ];
    operatingCostsRow.values = operatingCostsValues;

    // 折旧
    const depreciationRow = worksheet.getRow(currentRow++);
    const depreciationData = costData["折旧"] || {};
    const depreciationYearData = bl2(
      depreciationData.total?.depreciationValues || [],
    );
    const depreciationTotal = depreciationData.total?.depreciationTotal || 0;
    const depreciationValues = [
      seq++,
      "折旧",
      depreciationTotal !== 0 ? Number(depreciationTotal || 0).toFixed(2) : "",
      ...depreciationYearData,
    ];
    depreciationRow.values = depreciationValues;

    // 摊销
    const amortizationRow = worksheet.getRow(currentRow++);
    const amortizationData = costData["摊销"] || {};
    const amortizationYearData = bl2(
      amortizationData.total?.depreciationValues || [],
    );
    const amortizationTotal = amortizationData.total.depreciationTotal || 0;
    const amortizationValues = [
      seq++,
      "摊销",
      amortizationTotal !== 0 ? Number(amortizationTotal).toFixed(2) : "",
      ...amortizationYearData,
    ];
    amortizationRow.values = amortizationValues;

    // 利息
    const interestRow = worksheet.getRow(currentRow++);
    const interestData = costData["利息"] || { total: 0, year: [] };
    const interestYearData = getYearData(interestData, calculationPeriod);
    const interestTotal = interestData.total || 0;
    const interestValues = [
      seq++,
      "利息",
      interestTotal !== 0 ? Number(interestTotal.toFixed(2)) : "",
      ...interestYearData,
    ];
    interestRow.values = interestValues;

    // 总成本费用合计
    const totalCostRow = worksheet.getRow(currentRow++);
    const totalCostData = costData["总成本费用"] || { total: 0, year: [] };
    const totalCostYearData = getYearData(totalCostData, calculationPeriod);
    const totalCostTotal = totalCostData.total || 0;
    const totalCostValues = [
      "",
      "总成本费用合计",
      totalCostTotal !== 0 ? Number(totalCostTotal.toFixed(2)) : "",
      ...totalCostYearData,
    ];
    totalCostRow.values = totalCostValues;

    // 固定成本
    const fixedCostRow = worksheet.getRow(currentRow++);
    const fixedCostData = costData["固定成本"] || { total: 0, year: [] };
    const fixedCostYearData = getYearData(fixedCostData, calculationPeriod);
    const fixedCostTotal = fixedCostData.total || 0;
    const fixedCostValues = [
      "",
      "固定成本",
      fixedCostTotal !== 0 ? Number(fixedCostTotal.toFixed(2)) : "",
      ...fixedCostYearData,
    ];
    fixedCostRow.values = fixedCostValues;

    // 可变成本
    const variableCostRow = worksheet.getRow(currentRow++);
    const variableCostData = costData["可变成本"] || { total: 0, year: [] };
    const variableCostYearData = getYearData(
      variableCostData,
      calculationPeriod,
    );
    const variableCostTotal = variableCostData.total || 0;
    const variableCostValues = [
      "",
      "可变成本",
      variableCostTotal !== 0 ? Number(variableCostTotal.toFixed(2)) : "",
      ...variableCostYearData,
    ];
    variableCostRow.values = variableCostValues;

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出流动资金估算表
  static exportWorkingCapitalEstimate(projectData, workbook) {
    // 获取流动资金数据
    const workingCapitalData = projectData["流动资金估算"]?.["计算结果"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 15;
    const constructionPeriod = constru.constructionPeriod || 0;
    const operationPeriod = calculationPeriod - constructionPeriod;

    // 创建工作表
    const worksheet = workbook.addWorksheet("流动资金估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const lastCol = String.fromCharCode(65 + 2 + calculationPeriod); // A=65, 序号+项目+合计+年份列数
    worksheet.mergeCells(`A1:${lastCol}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "流动资金估算表";

    // 添加表头行 - 年份行
    const yearHeaderRow = worksheet.getRow(2);
    const yearHeaderValues = ["序号", "项目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      yearHeaderValues.push(year);
    }
    yearHeaderRow.values = yearHeaderValues;

    // 添加建设期/运营期行
    const periodRow = worksheet.getRow(3);
    const periodValues = ["-", "建设期/运营期", "-"];

    // 添加建设期/运营期标识
    for (let year = 1; year <= calculationPeriod; year++) {
      if (year <= constructionPeriod) {
        periodValues.push("建设期");
      } else {
        periodValues.push("运营期");
      }
    }
    periodRow.values = periodValues;

    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 合并建设期/运营期单元格
    if (constructionPeriod > 0) {
      const startCol = 4; // 第4列对应年份1
      const endCol = 3 + constructionPeriod; // 结束列
      worksheet.mergeCells(3, startCol, 3, endCol);
      const constructionCell = worksheet.getCell(3, startCol);
      constructionCell.value = "建设期";
      // 设置合并单元格的样式
      constructionCell.border = this.getDefaultBorder();
      constructionCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      constructionCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      constructionCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    if (operationPeriod > 0) {
      const startCol = 4 + constructionPeriod; // 第4+建设期列对应运营期开始
      const endCol = 3 + calculationPeriod; // 结束列
      worksheet.mergeCells(3, startCol, 3, endCol);
      const operationCell = worksheet.getCell(3, startCol);
      operationCell.value = "运营期";
      // 设置合并单元格的样式
      operationCell.border = this.getDefaultBorder();
      operationCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      operationCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      operationCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    // 辅助函数：获取年份数据，确保长度正确并格式化数值
    const getYearData = (data, period) => {
      const yearData = data?.year || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        let value = yearData[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        paddedData.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return paddedData;
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 应收账款
    const receivablesRow = worksheet.getRow(currentRow++);
    const receivablesData = workingCapitalData["receivables"] || {
      total: 0,
      year: [],
    };
    const receivablesYearData = getYearData(receivablesData, calculationPeriod);
    const receivablesTotal = Number(receivablesData.total) || 0;
    const receivablesValues = [
      seq++,
      "应收帐款",
      receivablesTotal !== 0 ? Number(receivablesTotal.toFixed(2)) : "",
      ...receivablesYearData,
    ];
    receivablesRow.values = receivablesValues;

    // 存货
    const inventoryRow = worksheet.getRow(currentRow++);
    const inventoryData = workingCapitalData["inventory"] || {
      total: 0,
      year: [],
    };
    const inventoryYearData = getYearData(inventoryData, calculationPeriod);
    const inventoryTotal = Number(inventoryData.total) || 0;
    const inventoryValues = [
      seq++,
      "存货",
      inventoryTotal !== 0 ? Number(inventoryTotal.toFixed(2)) : "",
      ...inventoryYearData,
    ];
    inventoryRow.values = inventoryValues;

    // 现金
    const cashRow = worksheet.getRow(currentRow++);
    const cashData = workingCapitalData["cash"] || { total: 0, year: [] };
    const cashYearData = getYearData(cashData, calculationPeriod);
    const cashTotal = Number(cashData.total) || 0;
    const cashValues = [
      seq++,
      "现金",
      cashTotal !== 0 ? Number(cashTotal.toFixed(2)) : "",
      ...cashYearData,
    ];
    cashRow.values = cashValues;

    // 应付账款
    const payablesRow = worksheet.getRow(currentRow++);
    const payablesData = workingCapitalData["payables"] || {
      total: 0,
      year: [],
    };
    const payablesYearData = getYearData(payablesData, calculationPeriod);
    const payablesTotal = Number(payablesData.total) || 0;
    const payablesValues = [
      seq++,
      "应付帐款",
      payablesTotal !== 0 ? Number(payablesTotal.toFixed(2)) : "",
      ...payablesYearData,
    ];
    payablesRow.values = payablesValues;

    // 流动资产
    const currentAssetsRow = worksheet.getRow(currentRow++);
    const currentAssetsData = workingCapitalData["currentAssets"] || {
      total: 0,
      year: [],
    };
    const currentAssetsYearData = getYearData(
      currentAssetsData,
      calculationPeriod,
    );
    const currentAssetsTotal = Number(currentAssetsData.total) || 0;
    const currentAssetsValues = [
      seq++,
      "流动资产",
      currentAssetsTotal !== 0 ? Number(currentAssetsTotal.toFixed(2)) : "",
      ...currentAssetsYearData,
    ];
    currentAssetsRow.values = currentAssetsValues;

    // 流动负债
    const currentLiabilitiesRow = worksheet.getRow(currentRow++);
    const currentLiabilitiesData = workingCapitalData["currentLiabilities"] || {
      total: 0,
      year: [],
    };
    const currentLiabilitiesYearData = getYearData(
      currentLiabilitiesData,
      calculationPeriod,
    );
    const currentLiabilitiesTotal = Number(currentLiabilitiesData.total) || 0;
    const currentLiabilitiesValues = [
      seq++,
      "流动负债",
      currentLiabilitiesTotal !== 0
        ? Number(currentLiabilitiesTotal.toFixed(2))
        : "",
      ...currentLiabilitiesYearData,
    ];
    currentLiabilitiesRow.values = currentLiabilitiesValues;

    // 流动资金需求
    const workingCapitalReqRow = worksheet.getRow(currentRow++);
    const workingCapitalReqData = workingCapitalData[
      "workingCapitalRequirement"
    ] || { total: 0, year: [] };
    const workingCapitalReqYearData = getYearData(
      workingCapitalReqData,
      calculationPeriod,
    );
    const workingCapitalReqTotal = Number(workingCapitalReqData.total) || 0;
    const workingCapitalReqValues = [
      "",
      "流动资金需求",
      workingCapitalReqTotal !== 0
        ? Number(workingCapitalReqTotal.toFixed(2))
        : "",
      ...workingCapitalReqYearData,
    ];
    workingCapitalReqRow.values = workingCapitalReqValues;

    // 流动资金贷款
    const workingCapitalLoanRow = worksheet.getRow(currentRow++);
    const workingCapitalLoanData = workingCapitalData["workingCapitalLoan"] || {
      total: 0,
      year: [],
    };
    const workingCapitalLoanYearData = getYearData(
      workingCapitalLoanData,
      calculationPeriod,
    );
    const workingCapitalLoanTotal = Number(workingCapitalLoanData.total) || 0;
    const workingCapitalLoanValues = [
      "",
      "流动资金贷款",
      workingCapitalLoanTotal !== 0
        ? Number(workingCapitalLoanTotal.toFixed(2))
        : "",
      ...workingCapitalLoanYearData,
    ];
    workingCapitalLoanRow.values = workingCapitalLoanValues;

    // 流动资金自有资金
    const workingCapitalEquityRow = worksheet.getRow(currentRow++);
    const workingCapitalEquityData = workingCapitalData[
      "workingCapitalEquity"
    ] || { total: 0, year: [] };
    const workingCapitalEquityYearData = getYearData(
      workingCapitalEquityData,
      calculationPeriod,
    );
    const workingCapitalEquityTotal =
      Number(workingCapitalEquityData.total) || 0;
    const workingCapitalEquityValues = [
      "",
      "流动资金自有资金",
      workingCapitalEquityTotal !== 0
        ? Number(workingCapitalEquityTotal.toFixed(2))
        : "",
      ...workingCapitalEquityYearData,
    ];
    workingCapitalEquityRow.values = workingCapitalEquityValues;

    // 自有流动资金增加额
    const equityIncreaseRow = worksheet.getRow(currentRow++);
    const equityIncreaseData = workingCapitalData["equityIncreaseThisYear"] || {
      total: 0,
      year: [],
    };
    const equityIncreaseYearData = getYearData(
      equityIncreaseData,
      calculationPeriod,
    );
    const equityIncreaseTotal = Number(equityIncreaseData.total) || 0;
    const equityIncreaseValues = [
      "",
      "自有流动资金增加额",
      equityIncreaseTotal !== 0 ? Number(equityIncreaseTotal.toFixed(2)) : "",
      ...equityIncreaseYearData,
    ];
    equityIncreaseRow.values = equityIncreaseValues;

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // // 获取年度数据的辅助函数
  static getYearData(data, category, period) {
    return Array.from(
      { length: period },
      (_, i) => data[category]?.years?.[i] || 0,
    );
  }

  // 导出原辅材料费用估算表
  static exportRawMaterials(projectData, workbook) {
    const costData = projectData["成本费用"] || {};
    const rawMaterialsData = costData["原辅材料"] || {};
    const materialsData = rawMaterialsData["materials"] || {};
    const auxiliaryData = rawMaterialsData["auxiliary"] || {};
    const otherData = rawMaterialsData["other"] || {};
    const totalData = rawMaterialsData["total"] || {};

    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 15;
    const constructionPeriod = constru.constructionPeriod || 0;
    const operationPeriod = calculationPeriod - constructionPeriod;

    // 创建工作表
    const worksheet = workbook.addWorksheet("原辅材料费用估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const lastCol = String.fromCharCode(65 + 2 + calculationPeriod); // A=65, 序号+项目+合计+年份列数
    worksheet.mergeCells(`A1:${lastCol}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "原辅材料费用估算表";

    // 添加表头行 - 年份行
    const yearHeaderRow = worksheet.getRow(2);
    const yearHeaderValues = ["序号", "项目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      yearHeaderValues.push(year);
    }
    yearHeaderRow.values = yearHeaderValues;

    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = yearHeaderRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 添加建设期/运营期行
    const periodRow = worksheet.getRow(3);
    const periodValues = ["-", "建设期/运营期", "-"];

    // 添加建设期/运营期标识
    for (let year = 1; year <= calculationPeriod; year++) {
      if (year <= constructionPeriod) {
        periodValues.push("建设期");
      } else {
        periodValues.push("运营期");
      }
    }
    periodRow.values = periodValues;
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 合并建设期/运营期单元格
    if (constructionPeriod > 0) {
      const startCol = 4; // 第4列对应年份1
      const endCol = 3 + constructionPeriod; // 结束列
      worksheet.mergeCells(3, startCol, 3, endCol);
      const constructionCell = worksheet.getCell(3, startCol);
      constructionCell.value = "建设期";
      // 设置合并单元格的样式
      constructionCell.border = this.getDefaultBorder();
      constructionCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      constructionCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      constructionCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    if (operationPeriod > 0) {
      const startCol = 4 + constructionPeriod; // 第4+建设期列对应运营期开始
      const endCol = 3 + calculationPeriod; // 结束列
      worksheet.mergeCells(3, startCol, 3, endCol);
      const operationCell = worksheet.getCell(3, startCol);
      operationCell.value = "运营期";
      // 设置合并单元格的样式
      operationCell.border = this.getDefaultBorder();
      operationCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      operationCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      operationCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    // 辅助函数：获取年份数据，确保长度正确并格式化数值
    const getYearData = (data, period) => {
      const yearData = data?.year || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        let value = yearData[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        paddedData.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return paddedData;
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 原材料费用
    const rawmaterialRow = worksheet.getRow(currentRow++);
    const rawmaterialYearData = getYearData(materialsData, calculationPeriod);
    const rawmaterialTotal = Number(materialsData.total) || 0;
    const rawmaterialValues = [
      seq++,
      "原材料费用",
      rawmaterialTotal !== 0 ? Number(rawmaterialTotal.toFixed(2)) : "",
      ...rawmaterialYearData,
    ];
    rawmaterialRow.values = rawmaterialValues;

    // 添加具体的原材料子项目
    const materialsList = materialsData.materialslist || [];
    materialsList.forEach((material, index) => {
      // 物料名称行
      const materialNameRow = worksheet.getRow(currentRow++);
      const materialYearData = material.year || [];
      const paddedMaterialYearData = [];
      for (let i = 0; i < calculationPeriod; i++) {
        const value = materialYearData[i] || 0;
        paddedMaterialYearData.push(
          value !== 0 ? Number(value.toFixed(2)) : "",
        );
      }

      const materialNameValues = [
        material.sequence, // 保留原始序号格式，如1.1、1.2
        `${material.name}（${material.unitPrice}元/${material.unit || "吨"}）`,
        material.total !== 0 ? Number(material.total.toFixed(2)) : "",
        ...paddedMaterialYearData,
      ];
      materialNameRow.values = materialNameValues;

      // 年耗量行
      const consumptionRow = worksheet.getRow(currentRow++);
      const consumptionYearData = material.consumption || [];
      const paddedConsumptionYearData = [];
      for (let i = 0; i < calculationPeriod; i++) {
        const value = consumptionYearData[i] || 0;
        paddedConsumptionYearData.push(
          value !== 0 ? Number(value.toFixed(2)) : "",
        );
      }

      const consumptionValues = [
        "", // 序号为空
        `年耗量（${material.unit}）`,
        material.consumptionTotal !== 0
          ? Number(material.consumptionTotal.toFixed(2))
          : "",
        ...paddedConsumptionYearData,
      ];
      consumptionRow.values = consumptionValues;

      // 进项税额行
      const taxRow = worksheet.getRow(currentRow++);
      const taxYearData = material.tax || [];
      const paddedTaxYearData = [];
      for (let i = 0; i < calculationPeriod; i++) {
        const value = taxYearData[i] || 0;
        paddedTaxYearData.push(value !== 0 ? Number(value.toFixed(2)) : "");
      }

      const taxValues = [
        "", // 序号为空
        `进项税额(${(material.taxRate * 100).toFixed(0)}%)`,
        material.taxTotal !== 0 ? Number(material.taxTotal.toFixed(2)) : "",
        ...paddedTaxYearData,
      ];
      taxRow.values = taxValues;
    });

    // 辅助材料费用
    const auxiliaryRow = worksheet.getRow(currentRow++);
    const auxiliaryYearData = getYearData(auxiliaryData, calculationPeriod);
    const auxiliaryTotal = Number(auxiliaryData.total) || 0;
    const auxiliaryValues = [
      seq++,
      "辅助材料费用",
      auxiliaryTotal !== 0 ? Number(auxiliaryTotal.toFixed(2)) : "",
      ...auxiliaryYearData,
    ];
    auxiliaryRow.values = auxiliaryValues;

    // 辅助材料进项税额
    const auxiliaryTaxRow = worksheet.getRow(currentRow++);
    const auxiliaryTaxYearData = auxiliaryData.tax || [];
    const paddedAuxiliaryTaxYearData = [];
    for (let i = 0; i < calculationPeriod; i++) {
      const value = auxiliaryTaxYearData[i] || 0;
      paddedAuxiliaryTaxYearData.push(
        value !== 0 ? Number(value.toFixed(2)) : "",
      );
    }

    const auxiliaryTaxValues = [
      "", // 序号为空
      `进项税额(${(auxiliaryData.taxRate * 100).toFixed(0)}%)`,
      auxiliaryData.taxTotal !== 0
        ? Number(auxiliaryData.taxTotal.toFixed(2))
        : "",
      ...paddedAuxiliaryTaxYearData,
    ];
    auxiliaryTaxRow.values = auxiliaryTaxValues;

    // 其他材料费用
    const otherRow = worksheet.getRow(currentRow++);
    const otherYearData = getYearData(otherData, calculationPeriod);
    const otherTotal = Number(otherData.total) || 0;
    const otherValues = [
      seq++,
      "其他材料费用",
      otherTotal !== 0 ? Number(otherTotal.toFixed(2)) : "",
      ...otherYearData,
    ];
    otherRow.values = otherValues;

    // 其他材料进项税额
    const otherTaxRow = worksheet.getRow(currentRow++);
    const otherTaxYearData = otherData.tax || [];
    const paddedOtherTaxYearData = [];
    for (let i = 0; i < calculationPeriod; i++) {
      const value = otherTaxYearData[i] || 0;
      paddedOtherTaxYearData.push(value !== 0 ? Number(value.toFixed(2)) : "");
    }

    const otherTaxValues = [
      "", // 序号为空
      `进项税额(${(otherData.taxRate * 100).toFixed(0)}%)`,
      otherData.taxTotal !== 0 ? Number(otherData.taxTotal.toFixed(2)) : "",
      ...paddedOtherTaxYearData,
    ];
    otherTaxRow.values = otherTaxValues;

    // 外购原辅材料合计
    const totalRow = worksheet.getRow(currentRow++);
    const totalYearData = getYearData(totalData, calculationPeriod);
    const totalTotal = Number(totalData.total) || 0;
    const totalValues = [
      seq++,
      "外购原辅材料合计",
      totalTotal !== 0 ? Number(totalTotal.toFixed(2)) : "",
      ...totalYearData,
    ];
    totalRow.values = totalValues;

    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }

    // 进项税额合计
    const totalTaxRow = worksheet.getRow(currentRow++);
    const totalTaxYearData = totalData.tax || [];
    const paddedTotalTaxYearData = [];
    for (let i = 0; i < calculationPeriod; i++) {
      const value = totalTaxYearData[i] || 0;
      paddedTotalTaxYearData.push(value !== 0 ? Number(value.toFixed(2)) : "");
    }

    const totalTaxValues = [
      seq++,
      "进项税额合计",
      totalData.taxTotal !== 0 ? Number(totalData.taxTotal.toFixed(2)) : "",
      ...paddedTotalTaxYearData,
    ];
    totalTaxRow.values = totalTaxValues;
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalTaxRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出燃料动力

  static exportFuelPower(projectData, workbook) {
    const fuelPowerData = projectData["成本费用"]?.["燃料动力"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 0;
    const constructionPeriod = constru.constructionPeriod || 0;
    const operationPeriod = constru.operationPeriod || 0;

    // 创建工作表
    const worksheet = workbook.addWorksheet("燃料动力费用估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项 目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const lastCol = String.fromCharCode(65 + 2 + calculationPeriod); // A=65, 序号+项目+合计+年份列数
    worksheet.mergeCells(`A1:${lastCol}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "燃料动力费用估算表";

    // 创建表头 - 年份行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项 目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = headerRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 创建建设期/运营期行
    const periodRow = worksheet.getRow(3);
    periodRow.values = ["-", "建设期/运营期", "-"];

    // 设置建设期/运营期列
    let currentCol = 4; // 从第4列开始（D列）
    for (let i = 1; i <= 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 建设期
    if (constructionPeriod > 0) {
      const endCol = currentCol + constructionPeriod - 1;
      const startCell = String.fromCharCode(64 + currentCol); // 64是A的ASCII码-1
      const endCell = String.fromCharCode(64 + endCol);
      worksheet.mergeCells(`C${periodRow.number}:C${periodRow.number}`); // 合并合计列
      worksheet.mergeCells(
        `${startCell}${periodRow.number}:${endCell}${periodRow.number}`,
      );
      const periodCell = periodRow.getCell(currentCol);
      periodCell.value = "建设期";
      // 设置合并单元格的样式
      periodCell.border = this.getDefaultBorder();
      periodCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      periodCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      periodCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      currentCol = endCol + 1;
    }

    // 运营期
    if (operationPeriod > 0) {
      const endCol = currentCol + operationPeriod - 1;
      const startCell = String.fromCharCode(64 + currentCol);
      const endCell = String.fromCharCode(64 + endCol);
      worksheet.mergeCells(
        `${startCell}${periodRow.number}:${endCell}${periodRow.number}`,
      );
      const periodCell = periodRow.getCell(currentCol);
      periodCell.value = "运营期";
      // 设置合并单元格的样式
      periodCell.border = this.getDefaultBorder();
      periodCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      periodCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      periodCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    // 建设期/运营期行样式将由ExcelStyles统一设置

    // 辅助函数：获取年份数据，确保长度正确并格式化数值
    const getYearData = (yearData, period) => {
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        const value = yearData?.[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        paddedData.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return paddedData;
    };

    // 辅助函数：获取年耗量数据，确保长度正确并格式化数值
    const getConsumptionData = (consumption, period) => {
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        const value = consumption?.[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        paddedData.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return paddedData;
    };

    // 辅助函数：获取税额数据，确保长度正确
    const getTaxData = (tax, period) => {
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        paddedData.push(tax?.[i] || 0);
      }
      return paddedData;
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 遍历燃料动力列表，添加详细信息
    const fuelPowerList = fuelPowerData.fuelpower || [];
    fuelPowerList.forEach((fuel) => {
      // 序号.燃料动力项目
      const fuelRow = worksheet.getRow(currentRow++);
      const fuelName = `${fuel.name}（${fuel.unitPrice}元/${
        fuel.unit || "吨"
      }）`;
      const fuelValues = [
        seq,
        fuelName,
        fuel.total || 0,
        ...getYearData(fuel.year, calculationPeriod),
      ];
      fuelRow.values = fuelValues;
      fuelRow.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      fuelRow.alignment = {
        vertical: "middle",
      };

      // 设置边框
      for (let col = 1; col <= fuelValues.length; col++) {
        const cell = fuelRow.getCell(col);
        cell.border = this.getDefaultBorder();
      }

      // 年耗量行
      const consumptionRow = worksheet.getRow(currentRow++);
      const consumptionValues = [
        "",
        `年耗量（${fuel.unit}）`,
        fuel.consumptionTotal || 0,
        ...getConsumptionData(fuel.consumption, calculationPeriod),
      ];
      consumptionRow.values = consumptionValues;
      consumptionRow.alignment = {
        vertical: "middle",
      };

      // 设置边框
      for (let col = 1; col <= consumptionValues.length; col++) {
        const cell = consumptionRow.getCell(col);
        cell.border = this.getDefaultBorder();
      }

      // 进项税额行
      const taxRow = worksheet.getRow(currentRow++);
      const taxRate = fuel.taxRate ? `${(fuel.taxRate * 100).toFixed(0)}%` : "";
      const taxValues = [
        "",
        `进项税额(${taxRate})`,
        fuel.taxTotal || 0,
        ...getTaxData(fuel.tax, calculationPeriod),
      ];
      taxRow.values = taxValues;
      taxRow.alignment = {
        vertical: "middle",
      };

      // 设置边框
      for (let col = 1; col <= taxValues.length; col++) {
        const cell = taxRow.getCell(col);
        cell.border = this.getDefaultBorder();
      }

      seq++;
    });

    // 外购燃料动力合计
    const totalFuelRow = worksheet.getRow(currentRow++);
    const totalFuelValues = [
      "",
      "外购燃料动力合计",
      fuelPowerData.total?.total || 0,
      ...getYearData(fuelPowerData.total?.year, calculationPeriod),
    ];
    totalFuelRow.values = totalFuelValues;
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalFuelRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }

    // 设置边框
    for (let col = 1; col <= totalFuelValues.length; col++) {
      const cell = totalFuelRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 进项税额合计
    const totalTaxRow = worksheet.getRow(currentRow++);
    const totalTaxValues = [
      "",
      "进项税额合计",
      fuelPowerData.total?.taxTotal || 0,
      ...getYearData(fuelPowerData.total?.tax, calculationPeriod),
    ];
    totalTaxRow.values = totalTaxValues;
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalTaxRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }

    // 设置边框
    for (let col = 1; col <= totalTaxValues.length; col++) {
      const cell = totalTaxRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 设置所有行高
    for (let rowNum = 1; rowNum <= currentRow; rowNum++) {
      worksheet.getRow(rowNum).height = 25;
    }

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出工资福利
  static exportWelfare(projectData, workbook) {
    const welfareData = projectData["成本费用"]?.["职工薪酬"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 0;
    const constructionPeriod = constru.constructionPeriod || 0;
    const operationPeriod = constru.operationPeriod || 0;

    // 创建工作表
    const worksheet = workbook.addWorksheet("工资福利费用估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项 目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const lastCol = String.fromCharCode(65 + 2 + calculationPeriod); // A=65, 序号+项目+合计+年份列数
    worksheet.mergeCells(`A1:${lastCol}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "工资福利费用估算表";

    // 创建表头 - 年份行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项 目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = headerRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 表头样式将由ExcelStyles统一设置

    // 创建建设期/运营期行
    const periodRow = worksheet.getRow(3);
    periodRow.values = ["-", "建设期/运营期", "-"];

    for (let i = 1; i <= 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 设置建设期/运营期列
    let currentCol = 4; // 从第4列开始（D列）

    // 建设期
    if (constructionPeriod > 0) {
      const endCol = currentCol + constructionPeriod - 1;
      const startCell = String.fromCharCode(64 + currentCol); // 64是A的ASCII码-1
      const endCell = String.fromCharCode(64 + endCol);
      worksheet.mergeCells(`C${periodRow.number}:C${periodRow.number}`); // 合并合计列
      worksheet.mergeCells(
        `${startCell}${periodRow.number}:${endCell}${periodRow.number}`,
      );
      const periodCell = periodRow.getCell(currentCol);
      periodCell.value = "建设期";
      currentCol = endCol + 1;
    }

    // 运营期
    if (operationPeriod > 0) {
      const endCol = currentCol + operationPeriod - 1;
      const startCell = String.fromCharCode(64 + currentCol);
      const endCell = String.fromCharCode(64 + endCol);
      worksheet.mergeCells(
        `${startCell}${periodRow.number}:${endCell}${periodRow.number}`,
      );
      const periodCell = periodRow.getCell(currentCol);
      periodCell.value = "运营期";
    }

    // 建设期/运营期行样式将由ExcelStyles统一设置

    // 辅助函数：获取年份数据，确保长度正确并格式化数值
    const getYearData = (yearData, period) => {
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        const value = yearData?.[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        paddedData.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return paddedData;
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 遍历工资福利列表，添加详细信息
    const welfareList = welfareData.welfareList || [];
    welfareList.forEach((job) => {
      // 1. 工种名称行
      const jobRow = worksheet.getRow(currentRow++);
      const jobValues = [
        seq,
        job.name,
        job.total || 0,
        ...getYearData(job.year, calculationPeriod),
      ];
      jobRow.values = jobValues;
      jobRow.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      jobRow.alignment = {
        vertical: "middle",
      };

      // 设置边框
      for (let col = 1; col <= jobValues.length; col++) {
        const cell = jobRow.getCell(col);
        cell.border = this.getDefaultBorder();
      }

      // 2. 年工资额行
      const salaryRow = worksheet.getRow(currentRow++);
      const salaryValues = [
        "",
        `年工资额（${job.people}人）`,
        job.salaryTotal || 0,
        ...getYearData(job.salaryYear, calculationPeriod),
      ];
      salaryRow.values = salaryValues;
      salaryRow.alignment = {
        vertical: "middle",
      };

      // 设置边框
      for (let col = 1; col <= salaryValues.length; col++) {
        const cell = salaryRow.getCell(col);
        cell.border = this.getDefaultBorder();
      }

      // 3. 人均年工资行
      const avgSalaryRow = worksheet.getRow(currentRow++);
      const avgSalaryValues = [
        "",
        "人均年工资",
        job.avgSalaryTotal || 0,
        ...getYearData(job.gz, calculationPeriod),
      ];
      avgSalaryRow.values = avgSalaryValues;
      avgSalaryRow.alignment = {
        vertical: "middle",
      };

      // 设置边框
      for (let col = 1; col <= avgSalaryValues.length; col++) {
        const cell = avgSalaryRow.getCell(col);
        cell.border = this.getDefaultBorder();
      }

      // 4. 福利费行
      const welfareExpenseRow = worksheet.getRow(currentRow++);
      const welfareRate = job.rate ? `${(job.rate * 100).toFixed(0)}%` : "";
      const welfareExpenseValues = [
        "",
        `福利费(${welfareRate})`,
        job.welfareTotal || 0,
        ...getYearData(job.welfareYear, calculationPeriod),
      ];
      welfareExpenseRow.values = welfareExpenseValues;
      welfareExpenseRow.alignment = {
        vertical: "middle",
      };

      // 设置边框
      for (let col = 1; col <= welfareExpenseValues.length; col++) {
        const cell = welfareExpenseRow.getCell(col);
        cell.border = this.getDefaultBorder();
      }

      seq++;
    });

    // 工资福利合计
    const totalWelfareRow = worksheet.getRow(currentRow++);
    const totalWelfareValues = [
      "",
      "工资福利合计",
      welfareData.total?.total || 0,
      ...getYearData(welfareData.total?.year, calculationPeriod),
    ];
    totalWelfareRow.values = totalWelfareValues;
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalWelfareRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }

    // 设置边框
    for (let col = 1; col <= totalWelfareValues.length; col++) {
      const cell = totalWelfareRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 工资费用合计
    const totalSalaryRow = worksheet.getRow(currentRow++);
    const totalSalaryValues = [
      "",
      "工资费用合计",
      welfareData.total?.salaryTotal || 0,
      ...getYearData(welfareData.total?.salaryYear, calculationPeriod),
    ];
    totalSalaryRow.values = totalSalaryValues;
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalSalaryRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }

    // 设置边框
    for (let col = 1; col <= totalSalaryValues.length; col++) {
      const cell = totalSalaryRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 福利费合计
    const totalWelfareExpenseRow = worksheet.getRow(currentRow++);
    const totalWelfareExpenseValues = [
      "",
      "福利费合计",
      welfareData.total?.welfareTotal || 0,
      ...getYearData(welfareData.total?.welfareYear, calculationPeriod),
    ];
    totalWelfareExpenseRow.values = totalWelfareExpenseValues;
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalWelfareExpenseRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }

    // 设置边框
    for (let col = 1; col <= totalWelfareExpenseValues.length; col++) {
      const cell = totalWelfareExpenseRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 设置所有行高
    for (let rowNum = 1; rowNum <= currentRow; rowNum++) {
      worksheet.getRow(rowNum).height = 25;
    }

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出折旧
  static exportDepreciation(projectData, workbook) {
    const depreciationData = projectData["成本费用"]?.["折旧"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 0;
    const constructionPeriod = constru.constructionPeriod || 0;
    const operationPeriod = constru.operationPeriod || 0;

    // 创建工作表
    const worksheet = workbook.addWorksheet("固定资产折旧费用估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项 目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const lastCol = String.fromCharCode(65 + 2 + calculationPeriod); // A=65, 序号+项目+合计+年份列数
    worksheet.mergeCells(`A1:${lastCol}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "固定资产折旧费用估算表";
    // 标题样式将由ExcelStyles统一设置

    // 创建表头 - 年份行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项 目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;

    // 仅为实际的列设置样式，而不是整行
    for (let col = 1; col <= headerValues.length; col++) {
      const cell = headerRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        bold: true,
        color: { argb: "FF34495E" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 浅蓝色背景
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      cell.border = this.getDefaultBorder();
    }

    headerRow.height = 25;

    // 创建建设期/运营期行
    const periodRow = worksheet.getRow(3);
    periodRow.values = ["-", "建设期/运营期", "-"];
    for (let i = 1; i <= 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 设置建设期/运营期列
    let currentCol = 4; // 从第4列开始（D列）

    // 建设期
    if (constructionPeriod > 0) {
      const endCol = currentCol + constructionPeriod - 1;
      const startCell = String.fromCharCode(64 + currentCol); // 64是A的ASCII码-1
      const endCell = String.fromCharCode(64 + endCol);
      worksheet.mergeCells(
        `${startCell}${periodRow.number}:${endCell}${periodRow.number}`,
      );
      const periodCell = periodRow.getCell(currentCol);
      periodCell.value = "建设期";
      // 设置合并单元格的样式
      periodCell.border = this.getDefaultBorder();
      periodCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      periodCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      periodCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      currentCol = endCol + 1;
    }

    // 运营期
    if (operationPeriod > 0) {
      const endCol = currentCol + operationPeriod - 1;
      const startCell = String.fromCharCode(64 + currentCol);
      const endCell = String.fromCharCode(64 + endCol);
      worksheet.mergeCells(
        `${startCell}${periodRow.number}:${endCell}${periodRow.number}`,
      );
      const periodCell = periodRow.getCell(currentCol);
      periodCell.value = "运营期";
      // 设置合并单元格的样式
      periodCell.border = this.getDefaultBorder();
      periodCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      periodCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      periodCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
    }

    // 辅助函数：获取年份数据，确保长度正确并格式化数值
    const getYearData = (yearData, period) => {
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        const value = yearData?.[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        paddedData.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return paddedData;
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 获取折旧数据
    const categories = depreciationData.categories || {};
    const buildingData = categories["房屋及建构筑物"] || {};
    const equipmentData = categories["设备"] || {};
    const totalData = depreciationData.total || {};

    // 1. 房屋及建构筑物
    // 房屋及建构筑物标题行
    const buildingHeaderRow = worksheet.getRow(currentRow++);
    const buildingHeaderValues = [
      seq,
      "房屋及建构筑物",
      "-",
      ...getYearData(buildingData.originalValues, calculationPeriod),
    ];
    buildingHeaderRow.values = buildingHeaderValues;
    buildingHeaderRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
    };
    buildingHeaderRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= buildingHeaderValues.length; col++) {
      const cell = buildingHeaderRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 房屋及建构筑物-原值行
    const buildingOriginalRow = worksheet.getRow(currentRow++);
    const buildingOriginalValues = [
      "",
      "原值",
      buildingData.originalValue || 0,
      ...getYearData(buildingData.originalValues, calculationPeriod),
    ];
    buildingOriginalRow.values = buildingOriginalValues;
    buildingOriginalRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= buildingOriginalValues.length; col++) {
      const cell = buildingOriginalRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 房屋及建构筑物-折旧行
    const buildingDepreciationRow = worksheet.getRow(currentRow++);
    const buildingDepreciationValues = [
      "",
      "折旧",
      buildingData.depreciationTotal || 0,
      ...getYearData(buildingData.depreciationValues, calculationPeriod),
    ];
    buildingDepreciationRow.values = buildingDepreciationValues;
    buildingDepreciationRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= buildingDepreciationValues.length; col++) {
      const cell = buildingDepreciationRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 房屋及建构筑物-净值行
    const buildingNetValueRow = worksheet.getRow(currentRow++);
    const buildingNetValueValues = [
      "",
      "净值",
      buildingData.NAVatEOP || 0,
      ...getYearData(buildingData.currentValues, calculationPeriod),
    ];
    buildingNetValueRow.values = buildingNetValueValues;
    buildingNetValueRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= buildingNetValueValues.length; col++) {
      const cell = buildingNetValueRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    seq++;

    // 2. 设备
    // 设备标题行
    const equipmentHeaderRow = worksheet.getRow(currentRow++);
    const equipmentHeaderValues = [
      seq,
      "设备",
      "-",
      ...getYearData(equipmentData.originalValues, calculationPeriod),
    ];
    equipmentHeaderRow.values = equipmentHeaderValues;
    equipmentHeaderRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
    };
    equipmentHeaderRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= equipmentHeaderValues.length; col++) {
      const cell = equipmentHeaderRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 设备-原值行
    const equipmentOriginalRow = worksheet.getRow(currentRow++);
    const equipmentOriginalValues = [
      "",
      "原值",
      equipmentData.originalValue || 0,
      ...getYearData(equipmentData.originalValues, calculationPeriod),
    ];
    equipmentOriginalRow.values = equipmentOriginalValues;
    equipmentOriginalRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= equipmentOriginalValues.length; col++) {
      const cell = equipmentOriginalRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 设备-折旧行
    const equipmentDepreciationRow = worksheet.getRow(currentRow++);
    const equipmentDepreciationValues = [
      "",
      "折旧",
      equipmentData.depreciationTotal || 0,
      ...getYearData(equipmentData.depreciationValues, calculationPeriod),
    ];
    equipmentDepreciationRow.values = equipmentDepreciationValues;
    equipmentDepreciationRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= equipmentDepreciationValues.length; col++) {
      const cell = equipmentDepreciationRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 设备-净值行
    const equipmentNetValueRow = worksheet.getRow(currentRow++);
    const equipmentNetValueValues = [
      "",
      "净值",
      equipmentData.NAVatEOP || 0,
      ...getYearData(equipmentData.currentValues, calculationPeriod),
    ];
    equipmentNetValueRow.values = equipmentNetValueValues;
    equipmentNetValueRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= equipmentNetValueValues.length; col++) {
      const cell = equipmentNetValueRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 3. 合计行
    // 原值合计
    const totalOriginalRow = worksheet.getRow(currentRow++);
    const totalOriginalValues = [
      "",
      "原值合计",
      (buildingData.originalValue || 0) + (equipmentData.originalValue || 0),
      ...getYearData(totalData.originalValues, calculationPeriod),
    ];
    totalOriginalRow.values = totalOriginalValues;

    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalOriginalRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }

    // totalOriginalRow.font = {
    //   name: "Microsoft YaHei",
    //   size: 12,
    //   bold: true,
    // };
    // totalOriginalRow.fill = {
    //   type: "pattern",
    //   pattern: "solid",
    //   fgColor: { argb: "FFE3F2FD" },
    // };
    // totalOriginalRow.alignment = {
    //   vertical: "middle",
    // };

    // 设置边框
    for (let col = 1; col <= totalOriginalValues.length; col++) {
      const cell = totalOriginalRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 折旧合计
    const totalDepreciationRow = worksheet.getRow(currentRow++);
    const totalDepreciationValues = [
      "",
      "折旧合计",
      totalData.depreciationTotal || 0,
      ...getYearData(totalData.depreciationValues, calculationPeriod),
    ];
    totalDepreciationRow.values = totalDepreciationValues;
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalDepreciationRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }
    // totalDepreciationRow.font = {
    //   name: "Microsoft YaHei",
    //   size: 12,
    //   bold: true,
    // };
    // totalDepreciationRow.fill = {
    //   type: "pattern",
    //   pattern: "solid",
    //   fgColor: { argb: "FFE3F2FD" },
    // };
    // totalDepreciationRow.alignment = {
    //   vertical: "middle",
    // };

    // 设置边框
    for (let col = 1; col <= totalDepreciationValues.length; col++) {
      const cell = totalDepreciationRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 净值合计
    const totalNetValueRow = worksheet.getRow(currentRow++);
    const totalNetValueValues = [
      "",
      "净值合计",
      totalData.NAVatEOP || 0,
      ...getYearData(totalData.currentValues, calculationPeriod),
    ];
    totalNetValueRow.values = totalNetValueValues;
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalNetValueRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }

    // totalNetValueRow.font = {
    //   name: "Microsoft YaHei",
    //   size: 12,
    //   bold: true,
    // };
    // totalNetValueRow.fill = {
    //   type: "pattern",
    //   pattern: "solid",
    //   fgColor: { argb: "FFE3F2FD" },
    // };
    // totalNetValueRow.alignment = {
    //   vertical: "middle",
    // };

    // 设置边框
    for (let col = 1; col <= totalNetValueValues.length; col++) {
      const cell = totalNetValueRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 设置所有行高
    for (let rowNum = 1; rowNum <= currentRow; rowNum++) {
      worksheet.getRow(rowNum).height = 25;
    }

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出摊销
  static exportAmortization(projectData, workbook) {
    const amortizationData = projectData["成本费用"]?.["摊销"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 0;
    const constructionPeriod = constru.constructionPeriod || 0;
    const operationPeriod = constru.operationPeriod || 0;

    // 创建工作表
    const worksheet = workbook.addWorksheet("其他费用摊销估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项 目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const lastCol = String.fromCharCode(65 + 2 + calculationPeriod); // A=65, 序号+项目+合计+年份列数
    worksheet.mergeCells(`A1:${lastCol}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "其他费用摊销估算表";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8F9FA" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    titleCell.border = this.getDefaultBorder();
    worksheet.getRow(1).height = 40;

    // 创建表头 - 年份行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项 目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;
    for (let year = 1; year <= calculationPeriod + 3; year++) {
      const cell = headerRow.getCell(year);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
        color: { argb: "FF34495E" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      cell.height = 25;
    }
    // 设置表头单元格边框
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = headerRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 创建建设期/运营期行
    const periodRow = worksheet.getRow(3);
    periodRow.values = ["-", "建设期/运营期", "-"];

    // 设置建设期/运营期列
    let currentCol = 4; // 从第4列开始（D列）
    for (let i = 1; i <= 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 建设期
    if (constructionPeriod > 0) {
      const endCol = currentCol + constructionPeriod - 1;
      const startCell = String.fromCharCode(64 + currentCol); // 64是A的ASCII码-1
      const endCell = String.fromCharCode(64 + endCol);
      worksheet.mergeCells(
        `${startCell}${periodRow.number}:${endCell}${periodRow.number}`,
      );
      const periodCell = periodRow.getCell(currentCol);
      periodCell.value = "建设期";
      currentCol = endCol + 1;
    }

    // 运营期
    if (operationPeriod > 0) {
      const endCol = currentCol + operationPeriod - 1;
      const startCell = String.fromCharCode(64 + currentCol);
      const endCell = String.fromCharCode(64 + endCol);
      worksheet.mergeCells(
        `${startCell}${periodRow.number}:${endCell}${periodRow.number}`,
      );
      const periodCell = periodRow.getCell(currentCol);
      periodCell.value = "运营期";
    }

    // 建设期/运营期行样式将由ExcelStyles统一设置

    // 辅助函数：获取年份数据，确保长度正确并格式化数值
    const getYearData = (yearData, period) => {
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        const value = yearData?.[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        paddedData.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return paddedData;
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 获取摊销数据
    const items = amortizationData.items || {};
    const intangibleData = items["无形资产"] || {};
    const otherData = items["其他资产"] || {};
    const totalData = amortizationData.total || {};

    // 1. 无形资产
    // 无形资产标题行
    const intangibleHeaderRow = worksheet.getRow(currentRow++);
    const intangibleHeaderValues = [
      seq,
      "无形资产",
      "-",
      ...getYearData(intangibleData.originalValues, calculationPeriod),
    ];
    intangibleHeaderRow.values = intangibleHeaderValues;
    intangibleHeaderRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
    };
    intangibleHeaderRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= intangibleHeaderValues.length; col++) {
      const cell = intangibleHeaderRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 无形资产-原值行
    const intangibleOriginalRow = worksheet.getRow(currentRow++);
    const intangibleOriginalValues = [
      "",
      "原值",
      intangibleData.originalValue || 0,
      ...getYearData(intangibleData.originalValues, calculationPeriod),
    ];
    intangibleOriginalRow.values = intangibleOriginalValues;
    intangibleOriginalRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= intangibleOriginalValues.length; col++) {
      const cell = intangibleOriginalRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 无形资产-摊销行
    const intangibleAmortizationRow = worksheet.getRow(currentRow++);
    const intangibleAmortizationValues = [
      "",
      "摊销",
      intangibleData.amortizationTotal || 0,
      ...getYearData(intangibleData.amortizationValues, calculationPeriod),
    ];
    intangibleAmortizationRow.values = intangibleAmortizationValues;
    intangibleAmortizationRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= intangibleAmortizationValues.length; col++) {
      const cell = intangibleAmortizationRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    seq++;

    // 2. 其他资产
    // 其他资产标题行
    const otherHeaderRow = worksheet.getRow(currentRow++);
    const otherHeaderValues = [
      seq,
      "其他资产",
      "-",
      ...getYearData(otherData.originalValues, calculationPeriod),
    ];
    otherHeaderRow.values = otherHeaderValues;
    otherHeaderRow.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
    };
    otherHeaderRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= otherHeaderValues.length; col++) {
      const cell = otherHeaderRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 其他资产-原值行
    const otherOriginalRow = worksheet.getRow(currentRow++);
    const otherOriginalValues = [
      "",
      "原值",
      otherData.originalValue || 0,
      ...getYearData(otherData.originalValues, calculationPeriod),
    ];
    otherOriginalRow.values = otherOriginalValues;
    otherOriginalRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= otherOriginalValues.length; col++) {
      const cell = otherOriginalRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 其他资产-摊销行
    const otherAmortizationRow = worksheet.getRow(currentRow++);
    const otherAmortizationValues = [
      "",
      "摊销",
      otherData.amortizationTotal || 0,
      ...getYearData(otherData.amortizationValues, calculationPeriod),
    ];
    otherAmortizationRow.values = otherAmortizationValues;
    otherAmortizationRow.alignment = {
      vertical: "middle",
    };

    // 设置边框
    for (let col = 1; col <= otherAmortizationValues.length; col++) {
      const cell = otherAmortizationRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 3. 合计行
    // 原值合计
    const totalOriginalRow = worksheet.getRow(currentRow++);
    const totalOriginalValues = [
      "",
      "原值合计",
      (intangibleData.originalValue || 0) + (otherData.originalValue || 0),
      ...getYearData(totalData.originalValues, calculationPeriod),
    ];
    totalOriginalRow.values = totalOriginalValues;
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalOriginalRow.getCell(col);
      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
    }

    // 设置边框
    for (let col = 1; col <= totalOriginalValues.length; col++) {
      const cell = totalOriginalRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 摊销合计
    const totalAmortizationRow = worksheet.getRow(currentRow++);
    const totalAmortizationValues = [
      "",
      "摊销合计",
      totalData.amortizationTotal || 0,
      ...getYearData(totalData.amortizationValues, calculationPeriod),
    ];
    totalAmortizationRow.values = totalAmortizationValues;
    for (let col = 1; col <= calculationPeriod + 3; col++) {
      const cell = totalAmortizationRow.getCell(col);

      cell.font = {
        name: "Microsoft YaHei",
        size: 12,
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      cell.alignment = {
        vertical: "middle",
      };
      // totalAmortizationRow.getCell(col)=cell
    }
    // 设置边框
    for (let col = 1; col <= totalAmortizationValues.length; col++) {
      const cell = totalAmortizationRow.getCell(col);
      cell.border = this.getDefaultBorder();
    }

    // 设置所有行高
    for (let rowNum = 1; rowNum <= currentRow; rowNum++) {
      worksheet.getRow(rowNum).height = 25;
    }

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }
  // 获取默认边框样式
  static getDefaultBorder() {
    return {
      top: { style: "thin", color: { argb: "FFD0D7DE" } },
      left: { style: "thin", color: { argb: "FFD0D7DE" } },
      bottom: { style: "thin", color: { argb: "FFD0D7DE" } },
      right: { style: "thin", color: { argb: "FFD0D7DE" } },
    };
  }

  // 导出营业收入税金及附加估算表
  static exportRevenueTaxSummary(projectData, workbook) {
    const revenueTaxData =
      projectData["营业收入税金及利润"]?.["营业收入及营业税金"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 15;
    const constructionPeriod = constru.constructionPeriod || 1;
    const operationPeriod = constru.operationPeriod || 14;

    // 创建工作表
    const worksheet = workbook.addWorksheet("营业收入税金及附加估算表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项 目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "营业收入税金及附加估算表";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
    };
    titleCell.alignment = {
      vertical: "middle",
      horizontal: "center",
    };

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项 目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;

    // 设置表头样式
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = headerRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 添加建设期/运营期行
    const periodRow = worksheet.getRow(3);
    const periodValues = ["-", "建设期/运营期", "-"];

    // 建设期年份
    for (let year = 1; year <= constructionPeriod; year++) {
      periodValues.push("建设期");
    }

    // 运营期年份
    for (let year = 1; year <= operationPeriod; year++) {
      periodValues.push("运营期");
    }

    // 确保数组长度与列数一致（序号+项目+合计+年份列数）
    for (let i = periodValues.length; i < 3 + calculationPeriod; i++) {
      periodValues.push("");
    }
    periodRow.values = periodValues;

    // 设置建设期/运营期行样式
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 合并建设期和运营期的列
    if (constructionPeriod > 0) {
      const constructionStart = "D";
      const constructionEnd = String.fromCharCode(67 + constructionPeriod); // D + constructionPeriod - 1
      worksheet.mergeCells(`${constructionStart}3:${constructionEnd}3`);
      // 为合并后的单元格设置样式
      const mergedCell = worksheet.getCell(constructionStart + "3");
      mergedCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      mergedCell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      mergedCell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      mergedCell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }
    if (operationPeriod > 0) {
      const operationStart = String.fromCharCode(68 + constructionPeriod); // E + constructionPeriod - 1
      const operationEnd = String.fromCharCode(
        67 + constructionPeriod + operationPeriod,
      ); // D + constructionPeriod + operationPeriod - 1
      worksheet.mergeCells(`${operationStart}3:${operationEnd}3`);
      // 为合并后的单元格设置样式
      const mergedCell = worksheet.getCell(operationStart + "3");
      mergedCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      mergedCell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      mergedCell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      mergedCell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 辅助函数：获取年份数据，确保长度正确并格式化数值
    const getYearData = (data, key, period) => {
      const yearData = data[key] || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        const value = yearData[i] || 0;
        // 确保value是数字类型
        const numValue = typeof value === "number" ? value : Number(value) || 0;
        // 如果值为0则显示为空字符串，否则保留两位小数
        paddedData.push(
          Math.abs(numValue) < 0.005 ? "" : Number(numValue.toFixed(2)),
        );
      }
      return paddedData;
    };

    // 获取相关数据
    const revenueList = revenueTaxData.revenue?.revenuelist || [];
    const otherInputData = revenueTaxData.otherinput || {};

    // 添加营业收入合计行
    let currentRow = 4;

    // 营业收入合计标题行
    const revenueTotalTitleRow = worksheet.getRow(currentRow);
    // 直接访问分年数据
    const revenueByYear = getYearData(
      revenueTaxData,
      "revenueByYear",
      calculationPeriod,
    );
    revenueTotalTitleRow.values = [
      "1",
      "营业收入",
      revenueTaxData.revenuetotal || 0,
      ...revenueByYear,
    ];

    currentRow++;

    // 添加各产品收入明细行
    revenueList.forEach((item, index) => {
      // 产品收入行
      const productRevenueRow = worksheet.getRow(currentRow);
      const productRevenueYearData = getYearData(
        item,
        "revenueByYear",
        calculationPeriod,
      );
      productRevenueRow.values = [
        `1.${index + 1}`,
        `${item.name}（${item.unitprice}元/${item.unit}）`,
        item.totalRevenue || 0,
        ...productRevenueYearData,
      ];
      currentRow++;

      // 年销量行
      const quantityRow = worksheet.getRow(currentRow);
      const quantityYearData = getYearData(
        item,
        "quantityByYear",
        calculationPeriod,
      );
      quantityRow.values = [
        "",
        `年销量（${item.unit}）`,
        item.totalquantity || 0,
        ...quantityYearData,
      ];
      currentRow++;

      // 销项税额行
      const taxRow = worksheet.getRow(currentRow);
      const taxYearData = getYearData(item, "taxByYear", calculationPeriod);
      taxRow.values = [
        "",
        `销项税额(${Math.round((item.tax || 0) * 100)}%)`,
        item.totalTax || 0,
        ...taxYearData,
      ];
      currentRow++;
    });

    // 添加其他收入行
    const otherInputTitleRow = worksheet.getRow(currentRow);
    const otherInputYearData = getYearData(
      otherInputData,
      "inputByYear",
      calculationPeriod,
    );
    otherInputTitleRow.values = [
      "2",
      "其他收入",
      otherInputData.total || 0,
      ...otherInputYearData,
    ];
    currentRow++;

    // 添加其他收入税额行
    const otherInputTaxRow = worksheet.getRow(currentRow);
    const otherInputTaxYearData = getYearData(
      otherInputData,
      "taxByYear",
      calculationPeriod,
    );
    otherInputTaxRow.values = [
      "",
      `其他收入税额(${Math.round((otherInputData.tax || 0) * 100)}%)`,
      otherInputData.taxTotal || 0,
      ...otherInputTaxYearData,
    ];
    currentRow++;

    // 添加总营业收入行
    const totalRevenueRow = worksheet.getRow(currentRow);
    const totalRevenueYearData = getYearData(
      revenueTaxData,
      "revenueByYear",
      calculationPeriod,
    );
    totalRevenueRow.values = [
      "3",
      "总收入",
      revenueTaxData.revenuetotal || 0,
      ...totalRevenueYearData,
    ];

    currentRow++;

    // 添加增值税行
    const vatRow = worksheet.getRow(currentRow);
    const vatYearData = getYearData(
      revenueTaxData,
      "valueddedtax",
      calculationPeriod,
    );
    vatRow.values = [
      "4",
      "增值税",
      revenueTaxData.valudedtotal || 0,
      ...vatYearData,
    ];
    currentRow++;

    // 添加销项税合计行
    const outputTaxTotalRow = worksheet.getRow(currentRow);
    const outputTaxYearData = getYearData(
      revenueTaxData,
      "outputtaxByYear",
      calculationPeriod,
    );
    outputTaxTotalRow.values = [
      "",
      "销项税合计",
      revenueTaxData.outputtotal || 0,
      ...outputTaxYearData,
    ];
    currentRow++;

    // 添加进项税合计行
    const inputTaxTotalRow = worksheet.getRow(currentRow);
    const inputTaxYearData = getYearData(
      revenueTaxData,
      "inputtaxByYear",
      calculationPeriod,
    );
    inputTaxTotalRow.values = [
      "",
      "进项税合计",
      revenueTaxData.inptotal || 0,
      ...inputTaxYearData,
    ];
    currentRow++;

    // 添加营业税金及附加行
    const taxAdditionRow = worksheet.getRow(currentRow);
    const taxAdditionYearData = getYearData(
      revenueTaxData,
      "additionaltaxByYear",
      calculationPeriod,
    );
    taxAdditionRow.values = [
      "5",
      "营业税金及附加",
      revenueTaxData.additionaltax || 0,
      ...taxAdditionYearData,
    ];
    currentRow++;

    // 添加消费税行
    const consumptionTaxRow = worksheet.getRow(currentRow);
    // 注意：消费税的分年数据键名在002.json中不存在，可能是consumptiontaxByYear或需要计算
    const consumptionTaxYearData = getYearData(
      revenueTaxData,
      "consumptiontaxByYear",
      calculationPeriod,
    );
    consumptionTaxRow.values = [
      "",
      "消费税",
      revenueTaxData.consumptiontaxTotal || 0,
      ...consumptionTaxYearData,
    ];
    currentRow++;

    // 添加城建维护税行
    const umcTaxRow = worksheet.getRow(currentRow);
    // 注意：城建维护税的分年数据键名在002.json中不存在，可能需要通过增值税计算
    const umcTaxYearData = getYearData(
      revenueTaxData,
      "UMCTaxByYear",
      calculationPeriod,
    );
    umcTaxRow.values = [
      "",
      "城建维护税",
      revenueTaxData.UMCTaxTotal || 0,
      ...umcTaxYearData,
    ];
    currentRow++;

    // 添加教育费附加行
    const educationTaxRow = worksheet.getRow(currentRow);
    // 注意：教育费附加的分年数据键名在002.json中不存在，可能需要通过增值税计算
    const educationTaxYearData = getYearData(
      revenueTaxData,
      "EducationtaxByYear",
      calculationPeriod,
    );
    educationTaxRow.values = [
      "",
      "教育费附加",
      revenueTaxData.EducationtaxTotal || 0,
      ...educationTaxYearData,
    ];

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);
  }
  // 导出利润表
  static exportProfit(projectData, workbook) {
    const revenueData = projectData["营业收入税金及利润"] || {};
    const profitData = revenueData["利润"] || {};
    const taxData = revenueData["营业收入及营业税金"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 15;
    const constructionPeriod = constru.constructionPeriod || 1;
    const operationPeriod = constru.operationPeriod || 14;

    // 创建工作表
    const worksheet = workbook.addWorksheet("利润表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: `第${year}年`, width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    );
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "利润表";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
    };
    titleCell.alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    titleCell.border = this.getDefaultBorder();

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(`${year}`);
    }
    headerRow.values = headerValues;

    // 设置表头样式
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = headerRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F5F7FA" },
      };
      cell.border = this.getDefaultBorder();
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
      };
    }

    // 添加建设期/运营期标识行
    const periodRow = worksheet.getRow(3);
    const periodValues = ["", "建设期/运营期", ""];

    // 建设期年份
    for (let year = 1; year <= constructionPeriod; year++) {
      periodValues.push("建设期");
    }

    // 运营期年份
    for (let year = 1; year <= operationPeriod; year++) {
      periodValues.push("运营期");
    }

    // 确保数组长度与列数一致（序号+项目+合计+年份列数）
    periodValues.length = 3 + calculationPeriod;
    periodRow.values = periodValues;

    // 设置建设期/运营期行样式
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      cell.border = this.getDefaultBorder();
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
      };
    }

    // 合并建设期和运营期的列
    if (constructionPeriod > 0) {
      const constructionStart = "D";
      const constructionEnd = String.fromCharCode(67 + constructionPeriod);
      worksheet.mergeCells(`${constructionStart}3:${constructionEnd}3`);
    }
    if (operationPeriod > 0) {
      const operationStart = String.fromCharCode(68 + constructionPeriod);
      const operationEnd = String.fromCharCode(67 + calculationPeriod);
      worksheet.mergeCells(`${operationStart}3:${operationEnd}3`);
    }

    let currentRow = 4;

    // 获取年份数据的辅助函数
    const getYearData = (data, key, period) => {
      const yearData = data[key] || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        paddedData.push(yearData[i] || 0);
      }
      return paddedData;
    };

    // 设置行样式的辅助函数
    const setRowStyle = (row, hasBold = false) => {
      if (hasBold) {
        for (let i = 1; i <= calculationPeriod + 3; i++) {
          const cell = row.getCell(i);
          cell.font = {
            bold: true,
          };
        }
      }
    };

    // 添加行的辅助函数
    const addRow = (seq, name, data, key, totalKey, hasBold = false) => {
      const row = worksheet.getRow(currentRow++);
      const yearData = getYearData(data, key, calculationPeriod);
      const totalValue = data[totalKey] || 0;

      row.values = [seq, name, totalValue, ...yearData];

      setRowStyle(row, hasBold);
    };

    // 1. 营业收入
    addRow("1", "营业收入", taxData, "revenueByYear", "revenuetotal");

    // 2. 营业税金及附加
    addRow(
      "2",
      "营业税金及附加",
      taxData,
      "additionaltaxByYear",
      "additionaltax",
    );

    // 3. 增值税
    addRow("3", "增值税", taxData, "valueddedtax", "valudedtotal");

    // 4. 总成本费用
    addRow("4", "总成本费用", profitData, "totalCostByYear", "totalCostTotal");

    // 5. 补贴收入
    addRow(
      "5",
      "补贴收入",
      profitData,
      "subsidyIncomeByYear",
      "subsidyIncomeTotal",
    );

    // 6. 利润总额
    addRow(
      "6",
      "利润总额",
      profitData,
      "profitBeforeTaxByYear",
      "profitBeforeTaxTotal",
      true,
    );

    // 7. 弥补以前年度亏损
    addRow(
      "7",
      "弥补以前年度亏损",
      profitData,
      "lossCompensationByYear",
      "lossCompensationTotal",
    );

    // 8. 应纳税所得额
    addRow(
      "8",
      "应纳税所得额",
      profitData,
      "taxableIncomeByYear",
      "taxableIncomeTotal",
    );

    // 9. 所得税
    addRow("9", "所得税", profitData, "incomeTaxYear", "incomeTaxTotal");

    // 10. 净利润
    addRow("10", "净利润", profitData, "netProfitYear", "netProfit", true);

    // 11. 期初未分配利润
    addRow(
      "11",
      "期初未分配利润",
      profitData,
      "beginningUndistributedProfitByYear",
      "beginningUndistributedProfitTotal",
    );

    // 12. 可供分配的利润
    addRow(
      "12",
      "可供分配的利润",
      profitData,
      "availableForDistributionByYear",
      "availableForDistributionTotal",
    );

    // 13. 法定盈余公积金
    addRow(
      "13",
      "法定盈余公积金",
      profitData,
      "法定盈余公积金Year",
      "法定盈余公积金Total",
    );

    // 14. 可供投资者分配的利润
    addRow(
      "14",
      "可供投资者分配的利润",
      profitData,
      "availableForInvestorsByYear",
      "availableForInvestorsTotal",
    );

    // 15. 应付优先股股利
    addRow(
      "15",
      "应付优先股股利",
      profitData,
      "preferredStockDividendByYear",
      "preferredStockDividendTotal",
    );

    // 16. 提取任意盈余公积金
    addRow(
      "16",
      "提取任意盈余公积金",
      profitData,
      "任意盈余公积金Year",
      "任意盈余公积金Total",
    );

    // 17. 应付普通股股利
    addRow(
      "17",
      "应付普通股股利",
      profitData,
      "commonDividendByYear",
      "commonDividendTotal",
    );

    // 18. 未分配利润
    addRow(
      "18",
      "未分配利润",
      profitData,
      "undistributedProfitByYear",
      "undistributedProfitTotal",
      true,
    );

    // 19. 息税前利润
    addRow("19", "息税前利润", profitData, "ebitByYear", "ebitTotal");

    // 20. 息税折旧摊销前利润
    addRow(
      "20",
      "息税折旧摊销前利润",
      profitData,
      "ebitdaByYear",
      "ebitdaTotal",
    );

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);
  }

  // 导出全部投资现金流量表
  static exportAllInvestmentCashFlow(projectData, workbook) {
    const allInvestmentCashFlowData =
      projectData["盈利能力分析"]?.["全部投资现金流量"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 15;
    const constructionPeriod = constru.constructionPeriod || 1;
    const operationPeriod = constru.operationPeriod || 14;

    // 创建工作表
    const worksheet = workbook.addWorksheet("全部投资现金流量表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    );
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "全部投资现金流量表";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 16,
      bold: true,
    };
    titleCell.alignment = {
      vertical: "middle",
      horizontal: "center",
    };

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;

    // 设置表头样式
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = headerRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = this.getDefaultBorder();
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 添加建设期/运营期标识行
    const periodRow = worksheet.getRow(3);
    const periodValues = ["", "建设期/运营期", ""];

    // 建设期年份
    for (let year = 1; year <= constructionPeriod; year++) {
      periodValues.push("建设期");
    }

    // 运营期年份
    for (let year = 1; year <= operationPeriod; year++) {
      periodValues.push("运营期");
    }

    // 确保数组长度与列数一致（序号+项目+合计+年份列数）
    for (let i = periodValues.length; i < 3 + calculationPeriod; i++) {
      periodValues.push("");
    }
    periodRow.values = periodValues;

    // 设置建设期/运营期行样式
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = this.getDefaultBorder();
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 合并建设期和运营期的列
    if (constructionPeriod > 0) {
      const constructionStart = "D";
      const constructionEnd = String.fromCharCode(67 + constructionPeriod);
      worksheet.mergeCells(`${constructionStart}3:${constructionEnd}3`);
    }
    if (operationPeriod > 0) {
      const operationStart = String.fromCharCode(68 + constructionPeriod);
      const operationEnd = String.fromCharCode(67 + calculationPeriod);
      worksheet.mergeCells(`${operationStart}3:${operationEnd}3`);
    }

    let currentRow = 4;

    // 获取年份数据的辅助函数
    const getYearData = (data, key, period) => {
      const yearData = data[key] || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        paddedData.push(yearData[i] || 0);
      }
      return paddedData;
    };

    // 设置行样式的辅助函数
    const setRowStyle = (row, hasBold = false) => {
      for (let i = 1; i <= calculationPeriod + 3; i++) {
        const cell = row.getCell(i);
        cell.border = this.getDefaultBorder();
        if (i === 2) {
          cell.alignment = {
            vertical: "middle",
            horizontal: "left",
          };
        } else {
          cell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };
        }
        if (hasBold) {
          cell.font = {
            bold: true,
          };
        }
      }
    };

    // 1. 现金流入
    const totalCashInflowsRow = worksheet.getRow(currentRow);
    const totalCashInflowsYearData = getYearData(
      allInvestmentCashFlowData,
      "totalCashInflowsByYear",
      calculationPeriod,
    );
    totalCashInflowsRow.values = [
      "1",
      "现金流入",
      allInvestmentCashFlowData.totalCashInflowsTotal || 0,
      ...totalCashInflowsYearData,
    ];
    setRowStyle(totalCashInflowsRow, true);
    currentRow++;

    // 1.1 营业收入
    const operatingRevenueRow = worksheet.getRow(currentRow);
    const operatingRevenueYearData = getYearData(
      allInvestmentCashFlowData,
      "operatingRevenueByYear",
      calculationPeriod,
    );
    operatingRevenueRow.values = [
      "1.1",
      "营业收入",
      allInvestmentCashFlowData.operatingRevenueTotal || 0,
      ...operatingRevenueYearData,
    ];
    setRowStyle(operatingRevenueRow);
    currentRow++;

    // 1.2 补贴收入
    const subsidyIncomeRow = worksheet.getRow(currentRow);
    const subsidyIncomeYearData = getYearData(
      allInvestmentCashFlowData,
      "subsidyIncomeByYear",
      calculationPeriod,
    );
    subsidyIncomeRow.values = [
      "1.2",
      "补贴收入",
      allInvestmentCashFlowData.subsidyIncomeTotal || 0,
      ...subsidyIncomeYearData,
    ];
    setRowStyle(subsidyIncomeRow);
    currentRow++;

    // 1.3 回收固定资产余值
    const recoveryFixedAssetsRow = worksheet.getRow(currentRow);
    const recoveryFixedAssetsYearData = getYearData(
      allInvestmentCashFlowData,
      "recoveryFixedAssetsByYear",
      calculationPeriod,
    );
    recoveryFixedAssetsRow.values = [
      "1.3",
      "回收固定资产余值",
      allInvestmentCashFlowData.recoveryFixedAssetsTotal || 0,
      ...recoveryFixedAssetsYearData,
    ];
    setRowStyle(recoveryFixedAssetsRow);
    currentRow++;

    // 1.4 回收流动资金
    const recoveryWorkingCapitalRow = worksheet.getRow(currentRow);
    const recoveryWorkingCapitalYearData = getYearData(
      allInvestmentCashFlowData,
      "recoveryWorkingCapitalByYear",
      calculationPeriod,
    );
    recoveryWorkingCapitalRow.values = [
      "1.4",
      "回收流动资金",
      allInvestmentCashFlowData.recoveryWorkingCapitalTotal || 0,
      ...recoveryWorkingCapitalYearData,
    ];
    setRowStyle(recoveryWorkingCapitalRow);
    currentRow++;

    // 2. 现金流出
    const totalCashOutflowsRow = worksheet.getRow(currentRow);
    const totalCashOutflowsYearData = getYearData(
      allInvestmentCashFlowData,
      "totalCashOutflowsByYear",
      calculationPeriod,
    );
    totalCashOutflowsRow.values = [
      "2",
      "现金流出",
      allInvestmentCashFlowData.totalCashOutflowsTotal || 0,
      ...totalCashOutflowsYearData,
    ];
    setRowStyle(totalCashOutflowsRow, true);
    currentRow++;

    // 2.1 总投资
    const totalInvestmentRow = worksheet.getRow(currentRow);
    const totalInvestmentYearData = getYearData(
      allInvestmentCashFlowData,
      "totalInvestmentByYear",
      calculationPeriod,
    );
    totalInvestmentRow.values = [
      "2.1",
      "总投资",
      allInvestmentCashFlowData.totalInvestmentTotal || 0,
      ...totalInvestmentYearData,
    ];
    setRowStyle(totalInvestmentRow);
    currentRow++;

    // 2.2 经营成本
    const operatingCostsRow = worksheet.getRow(currentRow);
    const operatingCostsYearData = getYearData(
      allInvestmentCashFlowData,
      "operatingCostsByYear",
      calculationPeriod,
    );
    operatingCostsRow.values = [
      "2.2",
      "经营成本",
      allInvestmentCashFlowData.operatingCostsTotal || 0,
      ...operatingCostsYearData,
    ];
    setRowStyle(operatingCostsRow);
    currentRow++;

    // 2.3 营业税金及附加
    const businessTaxRow = worksheet.getRow(currentRow);
    const businessTaxYearData = getYearData(
      allInvestmentCashFlowData,
      "businessTaxByYear",
      calculationPeriod,
    );
    businessTaxRow.values = [
      "2.3",
      "营业税金及附加",
      allInvestmentCashFlowData.businessTaxTotal || 0,
      ...businessTaxYearData,
    ];
    setRowStyle(businessTaxRow);
    currentRow++;

    // 2.4 增值税
    const vatRow = worksheet.getRow(currentRow);
    const vatYearData = getYearData(
      allInvestmentCashFlowData,
      "vatByYear",
      calculationPeriod,
    );
    vatRow.values = [
      "2.4",
      "增值税",
      allInvestmentCashFlowData.vatTotal || 0,
      ...vatYearData,
    ];
    setRowStyle(vatRow);
    currentRow++;

    // 2.5 所得税
    const incomeTaxRow = worksheet.getRow(currentRow);
    const incomeTaxYearData = getYearData(
      allInvestmentCashFlowData,
      "incomeTaxByYear",
      calculationPeriod,
    );
    incomeTaxRow.values = [
      "2.5",
      "所得税",
      allInvestmentCashFlowData.incomeTaxTotal || 0,
      ...incomeTaxYearData,
    ];
    setRowStyle(incomeTaxRow);
    currentRow++;

    // 3. 净现金流量
    const netCashFlowRow = worksheet.getRow(currentRow);
    const netCashFlowYearData = getYearData(
      allInvestmentCashFlowData,
      "netCashFlowByYear",
      calculationPeriod,
    );
    netCashFlowRow.values = [
      "3",
      "净现金流量",
      allInvestmentCashFlowData.netCashFlowTotal || 0,
      ...netCashFlowYearData,
    ];
    setRowStyle(netCashFlowRow, true);
    currentRow++;

    // 4. 折现净现金流量
    const discountedNetCashFlowRow = worksheet.getRow(currentRow);
    const discountedNetCashFlowYearData = getYearData(
      allInvestmentCashFlowData,
      "discountedNetCashFlowByYear",
      calculationPeriod,
    );
    discountedNetCashFlowRow.values = [
      "4",
      "折现净现金流量",
      allInvestmentCashFlowData.discountedNetCashFlowTotal || 0,
      ...discountedNetCashFlowYearData,
    ];
    setRowStyle(discountedNetCashFlowRow);
    currentRow++;

    // 5. 累计折现净现金流量
    const cumulativeDiscountedNetCashFlowRow = worksheet.getRow(currentRow);
    const cumulativeDiscountedNetCashFlowYearData = getYearData(
      allInvestmentCashFlowData,
      "cumulativeDiscountedNetCashFlowByYear",
      calculationPeriod,
    );
    cumulativeDiscountedNetCashFlowRow.values = [
      "5",
      "累计折现净现金流量",
      0, // 累计值不在合计列显示
      ...cumulativeDiscountedNetCashFlowYearData,
    ];
    setRowStyle(cumulativeDiscountedNetCashFlowRow);
    currentRow++;

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    // 返回工作表
    return worksheet;
  }

  // 导出资本金现金流量
  static exportCapitalCashFlow(projectData, workbook) {
    const cashFlowData =
      projectData["盈利能力分析"]?.["资本金现金流量表"] || {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 0;

    // 创建工作表
    const worksheet = workbook.addWorksheet("资本金现金流量");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项 目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "资本金现金流量";

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项 目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;

    // 添加建设期/运营期行
    const periodRow = worksheet.getRow(3);
    const constructionPeriod = constru.constructionPeriod || 0;

    // 初始化行值：序号、项目、合计列
    const periodValues = ["", "建设期/运营期", ""];

    // 确保数组长度与列数一致（序号+项目+合计+年份列数）
    periodValues.length = 3 + calculationPeriod;
    periodRow.values = periodValues;

    // 设置建设期/运营期行样式
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      cell.border = this.getDefaultBorder();
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
      };
    }

    // 设置建设期/运营期跨列居中（参考流动资金估算表实现）
    if (constructionPeriod > 0) {
      const startCol = 4; // 第4列对应年份1
      const endCol = 3 + constructionPeriod; // 结束列
      worksheet.mergeCells(3, startCol, 3, endCol);
      const constructionCell = worksheet.getCell(3, startCol);
      constructionCell.value = "建设期";
    }

    if (calculationPeriod > constructionPeriod) {
      const startCol = 4 + constructionPeriod; // 第4+建设期列对应运营期开始
      const endCol = 3 + calculationPeriod; // 结束列
      worksheet.mergeCells(3, startCol, 3, endCol);
      const operationCell = worksheet.getCell(3, startCol);
      operationCell.value = "运营期";
    }

    // 辅助函数：获取年份数据，确保长度正确
    const getYearData = (data, key, period) => {
      const yearData = data[key] || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        paddedData.push(yearData[i] || 0);
      }
      return paddedData;
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 1. 现金流入
    const inflowRow = worksheet.getRow(currentRow++);
    const inflowYearData = getYearData(
      cashFlowData,
      "cashInflowByYear",
      calculationPeriod,
    );
    const inflowValues = [
      seq++,
      "现金流入",
      cashFlowData.cashInflowTotal || 0,
      ...inflowYearData,
    ];
    inflowRow.values = inflowValues;

    // 1.1 营业收入
    const operatingRevenueRow = worksheet.getRow(currentRow++);
    const operatingRevenueYearData = getYearData(
      cashFlowData,
      "operatingRevenueByYear",
      calculationPeriod,
    );
    const operatingRevenueValues = [
      seq++,
      "营业收入",
      cashFlowData.operatingRevenueTotal || 0,
      ...operatingRevenueYearData,
    ];
    operatingRevenueRow.values = operatingRevenueValues;

    // 1.2 补贴收入
    const subsidyIncomeRow = worksheet.getRow(currentRow++);
    const subsidyIncomeYearData = getYearData(
      cashFlowData,
      "subsidyIncomeByYear",
      calculationPeriod,
    );
    const subsidyIncomeValues = [
      seq++,
      "补贴收入",
      cashFlowData.subsidyIncomeTotal || 0,
      ...subsidyIncomeYearData,
    ];
    subsidyIncomeRow.values = subsidyIncomeValues;

    // 1.3 其他流入
    const otherInflowRow = worksheet.getRow(currentRow++);
    const otherInflowYearData = getYearData(
      cashFlowData,
      "otherCashInflowByYear",
      calculationPeriod,
    );
    const otherInflowValues = [
      seq++,
      "其他流入",
      cashFlowData.otherCashInflowTotal || 0,
      ...otherInflowYearData,
    ];
    otherInflowRow.values = otherInflowValues;

    // 1.4 回收固定资产余值
    const recoveryFixedAssetsRow = worksheet.getRow(currentRow++);
    const recoveryFixedAssetsYearData = getYearData(
      cashFlowData,
      "recoveryFixedAssetsByYear",
      calculationPeriod,
    );
    const recoveryFixedAssetsValues = [
      seq++,
      "回收固定资产余值",
      cashFlowData.recoveryFixedAssetsTotal || 0,
      ...recoveryFixedAssetsYearData,
    ];
    recoveryFixedAssetsRow.values = recoveryFixedAssetsValues;

    // 1.5 回收流动资金
    const recoveryWorkingCapitalRow = worksheet.getRow(currentRow++);
    const recoveryWorkingCapitalYearData = getYearData(
      cashFlowData,
      "recoveryWorkingCapitalByYear",
      calculationPeriod,
    );
    const recoveryWorkingCapitalValues = [
      seq++,
      "回收流动资金",
      cashFlowData.recoveryWorkingCapitalTotal || 0,
      ...recoveryWorkingCapitalYearData,
    ];
    recoveryWorkingCapitalRow.values = recoveryWorkingCapitalValues;

    // 2. 现金流出
    const outflowRow = worksheet.getRow(currentRow++);
    const outflowYearData = getYearData(
      cashFlowData,
      "cashOutflowByYear",
      calculationPeriod,
    );
    const outflowValues = [
      seq++,
      "现金流出",
      cashFlowData.cashOutflowTotal || 0,
      ...outflowYearData,
    ];
    outflowRow.values = outflowValues;

    // 2.1 项目资本金投入
    const equityInvestmentRow = worksheet.getRow(currentRow++);
    const equityInvestmentYearData = getYearData(
      cashFlowData,
      "equityInvestmentByYear",
      calculationPeriod,
    );
    const equityInvestmentValues = [
      seq++,
      "项目资本金投入",
      cashFlowData.equityInvestmentTotal || 0,
      ...equityInvestmentYearData,
    ];
    equityInvestmentRow.values = equityInvestmentValues;

    // 2.2 借款本金偿还
    const debtPrincipalRepaymentRow = worksheet.getRow(currentRow++);
    const debtPrincipalRepaymentYearData = getYearData(
      cashFlowData,
      "debtPrincipalRepaymentByYear",
      calculationPeriod,
    );
    const debtPrincipalRepaymentValues = [
      seq++,
      "借款本金偿还",
      cashFlowData.debtPrincipalRepaymentTotal || 0,
      ...debtPrincipalRepaymentYearData,
    ];
    debtPrincipalRepaymentRow.values = debtPrincipalRepaymentValues;

    // 2.3 借款利息支付
    const interestPaymentRow = worksheet.getRow(currentRow++);
    const interestPaymentYearData = getYearData(
      cashFlowData,
      "利息支出ByYear",
      calculationPeriod,
    );
    const interestPaymentValues = [
      seq++,
      "借款利息支付",
      cashFlowData.利息支出Total || 0,
      ...interestPaymentYearData,
    ];
    interestPaymentRow.values = interestPaymentValues;

    // 2.4 经营成本
    const operatingCostRow = worksheet.getRow(currentRow++);
    const operatingCostYearData = getYearData(
      cashFlowData,
      "operatingCostByYear",
      calculationPeriod,
    );
    const operatingCostValues = [
      seq++,
      "经营成本",
      cashFlowData.operatingCostTotal || 0,
      ...operatingCostYearData,
    ];
    operatingCostRow.values = operatingCostValues;

    // 2.5 销售税金及附加
    const salesTaxRow = worksheet.getRow(currentRow++);
    const salesTaxYearData = getYearData(
      cashFlowData,
      "salesTaxByYear",
      calculationPeriod,
    );
    const salesTaxValues = [
      seq++,
      "销售税金及附加",
      cashFlowData.salesTaxTotal || 0,
      ...salesTaxYearData,
    ];
    salesTaxRow.values = salesTaxValues;

    // 2.6 增值税
    const vatRow = worksheet.getRow(currentRow++);
    const vatYearData = getYearData(
      cashFlowData,
      "vatByYear",
      calculationPeriod,
    );
    const vatValues = [
      seq++,
      "增值税",
      cashFlowData.vatTotal || 0,
      ...vatYearData,
    ];
    vatRow.values = vatValues;

    // 2.7 所得税
    const incomeTaxRow = worksheet.getRow(currentRow++);
    const incomeTaxYearData = getYearData(
      cashFlowData,
      "incomeTaxByYear",
      calculationPeriod,
    );
    const incomeTaxValues = [
      seq++,
      "所得税",
      cashFlowData.incomeTaxTotal || 0,
      ...incomeTaxYearData,
    ];
    incomeTaxRow.values = incomeTaxValues;

    // 2.8 其他流出
    const otherOutflowRow = worksheet.getRow(currentRow++);
    const otherOutflowYearData = getYearData(
      cashFlowData,
      "otherCashOutflowByYear",
      calculationPeriod,
    );
    const otherOutflowValues = [
      seq++,
      "其他流出",
      cashFlowData.otherCashOutflowTotal || 0,
      ...otherOutflowYearData,
    ];
    otherOutflowRow.values = otherOutflowValues;

    // 3. 净现金流量
    const netFlowRow = worksheet.getRow(currentRow++);
    const netFlowYearData = getYearData(
      cashFlowData,
      "netCashFlowByYear",
      calculationPeriod,
    );
    const netFlowValues = [
      seq++,
      "净现金流量",
      cashFlowData.netCashFlowTotal || 0,
      ...netFlowYearData,
    ];
    netFlowRow.values = netFlowValues;

    // 4. 折现净现金流量
    const discountedFlowRow = worksheet.getRow(currentRow++);
    const discountedFlowYearData = getYearData(
      cashFlowData,
      "discountedNetCashFlowByYear",
      calculationPeriod,
    );
    const discountedFlowValues = [
      seq++,
      "折现净现金流量",
      cashFlowData.discountedNetCashFlowTotal || 0,
      ...discountedFlowYearData,
    ];
    discountedFlowRow.values = discountedFlowValues;

    // 5. 累计折现净现金流量
    const cumulativeDiscountedRow = worksheet.getRow(currentRow++);
    const cumulativeDiscountedYearData = getYearData(
      cashFlowData,
      "cumulativeDiscountedNetCashFlowByYear",
      calculationPeriod,
    );
    const cumulativeDiscountedValues = [
      seq++,
      "累计折现净现金流量",
      cashFlowData.cumulativeDiscountedNetCashFlowByYear?.[
        calculationPeriod - 1
      ] || 0,
      ...cumulativeDiscountedYearData,
    ];
    cumulativeDiscountedRow.values = cumulativeDiscountedValues;

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出财务计划现金流量
  static exportFinancialPlanCashFlow(projectData, workbook) {
    // 根据comp.js中的函数，财务计划现金流量表的数据存储在"财务计划现金流量表"键下
    const financialData =
      projectData["盈利能力分析"]?.["财务计划现金流量表"] ||
      projectData["盈利能力分析"]?.["财务计划现金流量"] ||
      {};
    const constru = projectData["基础数据"] || {};
    const calculationPeriod = constru.calculationPeriod || 0;
    const constructionPeriod = constru.constructionPeriod || 0;
    const operationPeriod = constru.operationPeriod || 0;

    // 创建工作表
    const worksheet = workbook.addWorksheet("财务计划现金流量表");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项 目", width: 18 },
      { header: "合计", width: 15 },
    ];

    // 添加年份列
    for (let year = 1; year <= calculationPeriod; year++) {
      columns.push({ header: year.toString(), width: 10 });
    }

    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "财务计划现金流量表";

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项 目", "合计"];
    for (let year = 1; year <= calculationPeriod; year++) {
      headerValues.push(year);
    }
    headerRow.values = headerValues;

    // 设置表头样式
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = headerRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = this.getDefaultBorder();
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 添加建设期/经营期标识行
    const periodRow = worksheet.getRow(3);
    const periodValues = ["", "建设期/运营期", ""];
    for (let i = 0; i < calculationPeriod; i++) {
      periodValues.push(i < constructionPeriod ? "建设期" : "运营期");
    }

    // 确保数组长度与列数一致（序号+项目+合计+年份列数）
    for (let i = periodValues.length; i < 3 + calculationPeriod; i++) {
      periodValues.push("");
    }
    periodRow.values = periodValues;

    // 设置建设期/经营期行样式
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = this.getDefaultBorder();
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    // 设置建设期/经营期跨列居中
    if (constructionPeriod > 0) {
      const constructionStartCol = 4;
      const constructionEndCol = constructionStartCol + constructionPeriod - 1;
      worksheet.mergeCells(3, constructionStartCol, 3, constructionEndCol);
      const constructionCell = worksheet.getCell(3, constructionStartCol);
      constructionCell.value = "建设期";
      constructionCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      constructionCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      constructionCell.border = this.getDefaultBorder();
    }

    if (calculationPeriod > constructionPeriod) {
      const operationStartCol = 4 + constructionPeriod;
      const operationEndCol = 3 + calculationPeriod;
      worksheet.mergeCells(3, operationStartCol, 3, operationEndCol);
      const operationCell = worksheet.getCell(3, operationStartCol);
      operationCell.value = "运营期";
      operationCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      operationCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      operationCell.border = this.getDefaultBorder();
    }

    // 辅助函数：获取年份数据，确保长度正确
    const getYearData = (data, key, period) => {
      const yearData = data[key] || [];
      const paddedData = [];
      for (let i = 0; i < period; i++) {
        paddedData.push(yearData[i] || 0);
      }
      return paddedData;
    };

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 经营活动净现金流量
    const operatingNetFlowRow = worksheet.getRow(currentRow++);
    const operatingNetFlowYearData = getYearData(
      financialData,
      "operatingNetCashFlowByYear",
      calculationPeriod,
    );
    const operatingNetFlowValues = [
      seq++,
      "经营活动净现金流量",
      financialData.operatingNetCashFlowTotal || 0,
      ...operatingNetFlowYearData,
    ];
    operatingNetFlowRow.values = operatingNetFlowValues;

    // 经营活动现金流入
    const operatingInflowRow = worksheet.getRow(currentRow++);
    const operatingInflowYearData = getYearData(
      financialData,
      "operatingCashInflowByYear",
      calculationPeriod,
    );
    const operatingInflowValues = [
      "1.1",
      "现金流入",
      financialData.operatingCashInflowTotal || 0,
      ...operatingInflowYearData,
    ];
    operatingInflowRow.values = operatingInflowValues;

    // 营业收入
    const operatingRevenueRow = worksheet.getRow(currentRow++);
    const operatingRevenueYearData = getYearData(
      financialData,
      "operatingRevenueByYear",
      calculationPeriod,
    );
    const operatingRevenueValues = [
      "1.1.1",
      "营业收入",
      financialData.operatingRevenueTotal || 0,
      ...operatingRevenueYearData,
    ];
    operatingRevenueRow.values = operatingRevenueValues;

    // 补贴收入
    const subsidyIncomeRow = worksheet.getRow(currentRow++);
    const subsidyIncomeYearData = getYearData(
      financialData,
      "subsidyIncomeByYear",
      calculationPeriod,
    );
    const subsidyIncomeValues = [
      "1.1.2",
      "补贴收入",
      financialData.subsidyIncomeTotal || 0,
      ...subsidyIncomeYearData,
    ];
    subsidyIncomeRow.values = subsidyIncomeValues;

    // 其他流入
    const operatingOtherInflowRow = worksheet.getRow(currentRow++);
    const operatingOtherInflowYearData = getYearData(
      financialData,
      "operatingOtherCashInflowByYear",
      calculationPeriod,
    );
    const operatingOtherInflowValues = [
      "1.1.3",
      "其他流入",
      financialData.operatingOtherCashInflowTotal || 0,
      ...operatingOtherInflowYearData,
    ];
    operatingOtherInflowRow.values = operatingOtherInflowValues;

    // 经营活动现金流出
    const operatingOutflowRow = worksheet.getRow(currentRow++);
    const operatingOutflowYearData = getYearData(
      financialData,
      "operatingCashOutflowByYear",
      calculationPeriod,
    );
    const operatingOutflowValues = [
      "1.2",
      "现金流出",
      financialData.operatingCashOutflowTotal || 0,
      ...operatingOutflowYearData,
    ];
    operatingOutflowRow.values = operatingOutflowValues;

    // 经营成本
    const operatingCostRow = worksheet.getRow(currentRow++);
    const operatingCostYearData = getYearData(
      financialData,
      "operatingCostByYear",
      calculationPeriod,
    );
    const operatingCostValues = [
      "1.2.1",
      "经营成本",
      financialData.operatingCostTotal || 0,
      ...operatingCostYearData,
    ];
    operatingCostRow.values = operatingCostValues;

    // 营业税金及附加
    const salesTaxRow = worksheet.getRow(currentRow++);
    const salesTaxYearData = getYearData(
      financialData,
      "salesTaxByYear",
      calculationPeriod,
    );
    const salesTaxValues = [
      "1.2.2",
      "营业税金及附加",
      financialData.salesTaxTotal || 0,
      ...salesTaxYearData,
    ];
    salesTaxRow.values = salesTaxValues;

    // 增值税
    const vatRow = worksheet.getRow(currentRow++);
    const vatYearData = getYearData(
      financialData,
      "vatByYear",
      calculationPeriod,
    );
    const vatValues = [
      "1.2.3",
      "增值税",
      financialData.vatTotal || 0,
      ...vatYearData,
    ];
    vatRow.values = vatValues;

    // 所得税
    const incomeTaxRow = worksheet.getRow(currentRow++);
    const incomeTaxYearData = getYearData(
      financialData,
      "incomeTaxByYear",
      calculationPeriod,
    );
    const incomeTaxValues = [
      "1.2.4",
      "所得税",
      financialData.incomeTaxTotal || 0,
      ...incomeTaxYearData,
    ];
    incomeTaxRow.values = incomeTaxValues;

    // 其他流出
    const operatingOtherOutflowRow = worksheet.getRow(currentRow++);
    const operatingOtherOutflowYearData = getYearData(
      financialData,
      "operatingOtherCashOutflowByYear",
      calculationPeriod,
    );
    const operatingOtherOutflowValues = [
      "1.2.5",
      "其他流出",
      financialData.operatingOtherCashOutflowTotal || 0,
      ...operatingOtherOutflowYearData,
    ];
    operatingOtherOutflowRow.values = operatingOtherOutflowValues;

    // 投资活动净现金流量
    const investmentNetFlowRow = worksheet.getRow(currentRow++);
    const investmentNetFlowYearData = getYearData(
      financialData,
      "investmentNetCashFlowByYear",
      calculationPeriod,
    );
    const investmentNetFlowValues = [
      seq++,
      "投资活动净现金流量",
      financialData.investmentNetCashFlowTotal || 0,
      ...investmentNetFlowYearData,
    ];
    investmentNetFlowRow.values = investmentNetFlowValues;

    // 投资活动现金流入
    const investmentInflowRow = worksheet.getRow(currentRow++);
    const investmentInflowYearData = getYearData(
      financialData,
      "investmentCashInflowByYear",
      calculationPeriod,
    );
    const investmentInflowValues = [
      "2.1",
      "现金流入",
      financialData.investmentCashInflowTotal || 0,
      ...investmentInflowYearData,
    ];
    investmentInflowRow.values = investmentInflowValues;

    // 回收固定资产余值
    const recoveryFixedAssetsRow = worksheet.getRow(currentRow++);
    const recoveryFixedAssetsYearData = getYearData(
      financialData,
      "recoveryFixedAssetsByYear",
      calculationPeriod,
    );
    const recoveryFixedAssetsValues = [
      "2.1.1",
      "回收固定资产余值",
      financialData.recoveryFixedAssetsTotal || 0,
      ...recoveryFixedAssetsYearData,
    ];
    recoveryFixedAssetsRow.values = recoveryFixedAssetsValues;

    // 回收流动资金
    const recoveryWorkingCapitalRow = worksheet.getRow(currentRow++);
    const recoveryWorkingCapitalYearData = getYearData(
      financialData,
      "recoveryWorkingCapitalByYear",
      calculationPeriod,
    );
    const recoveryWorkingCapitalValues = [
      "2.1.2",
      "回收流动资金",
      financialData.recoveryWorkingCapitalTotal || 0,
      ...recoveryWorkingCapitalYearData,
    ];
    recoveryWorkingCapitalRow.values = recoveryWorkingCapitalValues;

    // 投资活动现金流出
    const investmentOutflowRow = worksheet.getRow(currentRow++);
    const investmentOutflowYearData = getYearData(
      financialData,
      "investmentCashOutflowByYear",
      calculationPeriod,
    );
    const investmentOutflowValues = [
      "2.2",
      "现金流出",
      financialData.investmentCashOutflowTotal || 0,
      ...investmentOutflowYearData,
    ];
    investmentOutflowRow.values = investmentOutflowValues;

    // 建设投资
    const constructionInvestmentRow = worksheet.getRow(currentRow++);
    const constructionInvestmentYearData = getYearData(
      financialData,
      "constructionInvestmentByYear",
      calculationPeriod,
    );
    const constructionInvestmentValues = [
      "2.2.1",
      "建设投资",
      financialData.constructionInvestmentTotal || 0,
      ...constructionInvestmentYearData,
    ];
    constructionInvestmentRow.values = constructionInvestmentValues;

    // 维持运营投资
    const operationMaintenanceInvestmentRow = worksheet.getRow(currentRow++);
    const operationMaintenanceInvestmentYearData = getYearData(
      financialData,
      "operationMaintenanceInvestmentByYear",
      calculationPeriod,
    );
    const operationMaintenanceInvestmentValues = [
      "2.2.2",
      "维持运营投资",
      financialData.operationMaintenanceInvestmentTotal || 0,
      ...operationMaintenanceInvestmentYearData,
    ];
    operationMaintenanceInvestmentRow.values =
      operationMaintenanceInvestmentValues;

    // 流动资金
    const workingCapitalRow = worksheet.getRow(currentRow++);
    const workingCapitalYearData = getYearData(
      financialData,
      "workingCapitalByYear",
      calculationPeriod,
    );
    const workingCapitalValues = [
      "2.2.3",
      "流动资金",
      financialData.workingCapitalTotal || 0,
      ...workingCapitalYearData,
    ];
    workingCapitalRow.values = workingCapitalValues;

    // 其他流出
    const investmentOtherOutflowRow = worksheet.getRow(currentRow++);
    const investmentOtherOutflowYearData = getYearData(
      financialData,
      "investmentOtherCashOutflowByYear",
      calculationPeriod,
    );
    const investmentOtherOutflowValues = [
      "2.2.4",
      "其他流出",
      financialData.investmentOtherCashOutflowTotal || 0,
      ...investmentOtherOutflowYearData,
    ];
    investmentOtherOutflowRow.values = investmentOtherOutflowValues;

    // 筹资活动净现金流量
    const financingNetFlowRow = worksheet.getRow(currentRow++);
    const financingNetFlowYearData = getYearData(
      financialData,
      "financingNetCashFlowByYear",
      calculationPeriod,
    );
    const financingNetFlowValues = [
      seq++,
      "筹资活动净现金流量",
      financialData.financingNetCashFlowTotal || 0,
      ...financingNetFlowYearData,
    ];
    financingNetFlowRow.values = financingNetFlowValues;

    // 筹资活动现金流入
    const financingInflowRow = worksheet.getRow(currentRow++);
    const financingInflowYearData = getYearData(
      financialData,
      "financingCashInflowByYear",
      calculationPeriod,
    );
    const financingInflowValues = [
      "3.1",
      "现金流入",
      financialData.financingCashInflowTotal || 0,
      ...financingInflowYearData,
    ];
    financingInflowRow.values = financingInflowValues;

    // 项目资本金投入
    const equityInvestmentRow = worksheet.getRow(currentRow++);
    const equityInvestmentYearData = getYearData(
      financialData,
      "equityInvestmentByYear",
      calculationPeriod,
    );
    const equityInvestmentValues = [
      "3.1.1",
      "项目资本金投入",
      financialData.equityInvestmentTotal || 0,
      ...equityInvestmentYearData,
    ];
    equityInvestmentRow.values = equityInvestmentValues;

    // 建设投资借款
    const constructionLoanRow = worksheet.getRow(currentRow++);
    const constructionLoanYearData = getYearData(
      financialData,
      "constructionLoanByYear",
      calculationPeriod,
    );
    const constructionLoanValues = [
      "3.1.2",
      "建设投资借款(不含债券)",
      financialData.constructionLoanTotal || 0,
      ...constructionLoanYearData,
    ];
    constructionLoanRow.values = constructionLoanValues;

    // 流动资金借款
    const workingCapitalLoanRow = worksheet.getRow(currentRow++);
    const workingCapitalLoanYearData = getYearData(
      financialData,
      "workingCapitalLoanByYear",
      calculationPeriod,
    );
    const workingCapitalLoanValues = [
      "3.1.3",
      "流动资金借款",
      financialData.workingCapitalLoanTotal || 0,
      ...workingCapitalLoanYearData,
    ];
    workingCapitalLoanRow.values = workingCapitalLoanValues;

    // 债券
    const bondRow = worksheet.getRow(currentRow++);
    const bondYearData = getYearData(
      financialData,
      "bondByYear",
      calculationPeriod,
    );
    const bondValues = [
      "3.1.4",
      "债券",
      financialData.bondTotal || 0,
      ...bondYearData,
    ];
    bondRow.values = bondValues;

    // 短期借款
    const shortTermLoanRow = worksheet.getRow(currentRow++);
    const shortTermLoanYearData = getYearData(
      financialData,
      "shortTermLoanByYear",
      calculationPeriod,
    );
    const shortTermLoanValues = [
      "3.1.5",
      "短期借款",
      financialData.shortTermLoanTotal || 0,
      ...shortTermLoanYearData,
    ];
    shortTermLoanRow.values = shortTermLoanValues;

    // 其他流入
    const financingOtherInflowRow = worksheet.getRow(currentRow++);
    const financingOtherInflowYearData = getYearData(
      financialData,
      "financingOtherCashInflowByYear",
      calculationPeriod,
    );
    const financingOtherInflowValues = [
      "3.1.6",
      "其他流入",
      financialData.financingOtherCashInflowTotal || 0,
      ...financingOtherInflowYearData,
    ];
    financingOtherInflowRow.values = financingOtherInflowValues;

    // 筹资活动现金流出
    const financingOutflowRow = worksheet.getRow(currentRow++);
    const financingOutflowYearData = getYearData(
      financialData,
      "financingCashOutflowByYear",
      calculationPeriod,
    );
    const financingOutflowValues = [
      "3.2",
      "现金流出",
      financialData.financingCashOutflowTotal || 0,
      ...financingOutflowYearData,
    ];
    financingOutflowRow.values = financingOutflowValues;

    // 各种利息支出
    const interestExpenseRow = worksheet.getRow(currentRow++);
    const interestExpenseYearData = getYearData(
      financialData,
      "利息支出ByYear",
      calculationPeriod,
    );
    const interestExpenseValues = [
      "3.2.1",
      "各种利息支出",
      financialData.利息支出Total || 0,
      ...interestExpenseYearData,
    ];
    interestExpenseRow.values = interestExpenseValues;

    // 偿还债务本金
    const debtPrincipalRepaymentRow = worksheet.getRow(currentRow++);
    const debtPrincipalRepaymentYearData = getYearData(
      financialData,
      "debtPrincipalRepaymentByYear",
      calculationPeriod,
    );
    const debtPrincipalRepaymentValues = [
      "3.2.2",
      "偿还债务本金",
      financialData.debtPrincipalRepaymentTotal || 0,
      ...debtPrincipalRepaymentYearData,
    ];
    debtPrincipalRepaymentRow.values = debtPrincipalRepaymentValues;

    // 应付利润(股利分配)
    const dividendsRow = worksheet.getRow(currentRow++);
    const dividendsYearData = getYearData(
      financialData,
      "dividendsByYear",
      calculationPeriod,
    );
    const dividendsValues = [
      "3.2.3",
      "应付利润(股利分配)",
      financialData.dividendsTotal || 0,
      ...dividendsYearData,
    ];
    dividendsRow.values = dividendsValues;

    // 其他流出
    const financingOtherOutflowRow = worksheet.getRow(currentRow++);
    const financingOtherOutflowYearData = getYearData(
      financialData,
      "financingOtherCashOutflowByYear",
      calculationPeriod,
    );
    const financingOtherOutflowValues = [
      "3.2.4",
      "其他流出",
      financialData.financingOtherCashOutflowTotal || 0,
      ...financingOtherOutflowYearData,
    ];
    financingOtherOutflowRow.values = financingOtherOutflowValues;

    // 净现金流量
    const netCashFlowRow = worksheet.getRow(currentRow++);
    const netCashFlowYearData = getYearData(
      financialData,
      "netCashFlowByYear",
      calculationPeriod,
    );
    const netCashFlowValues = [
      seq++,
      "净现金流量",
      financialData.netCashFlowTotal || 0,
      ...netCashFlowYearData,
    ];
    netCashFlowRow.values = netCashFlowValues;

    // 累计盈余资金
    const cumulativeSurplusFundRow = worksheet.getRow(currentRow++);
    const cumulativeSurplusFundYearData = getYearData(
      financialData,
      "surplusFundByYear",
      calculationPeriod,
    );
    const cumulativeSurplusFundValues = [
      seq++,
      "累计盈余资金",
      financialData.surplusFundTotal || 0,
      ...cumulativeSurplusFundYearData,
    ];
    cumulativeSurplusFundRow.values = cumulativeSurplusFundValues;

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出盈亏平衡分析
  static exportBreakevenAnalysis(projectData, workbook) {
    const breakEvenData = projectData["不确定性分析"]?.["盈亏平衡分析"] || {};
    const jichu = projectData["基础数据"] || {};
    const calculationPeriod = jichu.calculationPeriod || 15;
    const constructionPeriod = jichu.constructionPeriod || 1;

    // 创建工作表
    const worksheet = workbook.addWorksheet("盈亏平衡分析");

    // 设置列宽
    const columns = [
      { header: "序号", width: 8 },
      { header: "项目", width: 18 },
      { header: "合计", width: 15 },
    ];
    for (let i = 1; i <= calculationPeriod; i++) {
      columns.push({ header: `${i}`, width: 10 });
    }
    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(65 + columns.length - 1); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "盈亏平衡分析表";
    // 设置标题无边框
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 10, // 修改为10px
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8F9FA" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["序号", "项目", "合计"];
    for (let i = 1; i <= calculationPeriod; i++) {
      headerValues.push(`${i}`);
    }
    headerRow.values = headerValues;

    // 添加建设期/经营期标识行
    const periodRow = worksheet.getRow(3);
    const periodValues = ["", "建设期/运营期", ""];
    for (let i = 0; i < calculationPeriod; i++) {
      periodValues.push(i < constructionPeriod ? "建设期" : "运营期");
    }
    periodRow.values = periodValues;
    for (let i = 1; i <= calculationPeriod + 3; i++) {
      const cell = periodRow.getCell(i);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" }, // 统一使用表头背景色
      };
      cell.border = {
        top: { style: "thin", color: { argb: "E4E7ED" } },
        left: { style: "thin", color: { argb: "E4E7ED" } },
        bottom: { style: "thin", color: { argb: "E4E7ED" } },
        right: { style: "thin", color: { argb: "E4E7ED" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.font = {
        bold: true,
        name: "Microsoft YaHei",
        size: 9, // 统一使用9px字体
        color: { argb: "FF34495E" },
      };
    }

    periodRow.font = {
      name: "Microsoft YaHei",
      size: 9, // 字体大小为9px
      bold: true,
    };

    // 设置建设期/经营期跨列居中
    if (constructionPeriod > 0) {
      const constructionStartCol = 4;
      const constructionEndCol = constructionStartCol + constructionPeriod - 1;
      worksheet.mergeCells(3, constructionStartCol, 3, constructionEndCol);
      const constructionCell = worksheet.getCell(3, constructionStartCol);
      constructionCell.value = "建设期";
      constructionCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      constructionCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      constructionCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      constructionCell.border = this.getDefaultBorder();
    }

    if (calculationPeriod > constructionPeriod) {
      const operationStartCol = 4 + constructionPeriod;
      const operationEndCol = 3 + calculationPeriod;
      worksheet.mergeCells(3, operationStartCol, 3, operationEndCol);
      const operationCell = worksheet.getCell(3, operationStartCol);
      operationCell.value = "运营期";
      operationCell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      operationCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F0F9FF" },
      };
      operationCell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
        color: { argb: "FF34495E" },
      };
      operationCell.border = this.getDefaultBorder();
    }

    // 添加数据行
    let seq = 1;
    let currentRow = 4;

    // 定义盈亏平衡分析指标配置 - 支持动态指标数量
    const breakevenIndicators = [
      {
        key: "annualRevenueByYear",
        name: "年销售收入",
        showTotal: true,
        format: "number",
      },
      {
        key: "salesTaxByYear",
        name: "年销售税金及附加",
        showTotal: true,
        format: "number",
      },
      { key: "vatByYear", name: "年增值税", showTotal: true, format: "number" },
      {
        key: "variableCostByYear",
        name: "年可变成本",
        showTotal: true,
        format: "number",
      },
      {
        key: "fixedCostByYear",
        name: "年固定总成本",
        showTotal: true,
        format: "number",
      },
      {
        key: "bepUtilizationRateByYear",
        name: "BEP(%)",
        showTotal: false,
        format: "percentage",
      },
    ];

    // 动态生成指标行
    breakevenIndicators.forEach((indicator) => {
      // 检查数据是否存在
      if (breakEvenData[indicator.key]) {
        const dataRow = worksheet.getRow(currentRow++);

        // 计算合计值
        let totalValue = 0;
        if (indicator.showTotal) {
          totalValue =
            breakEvenData[indicator.key].reduce((sum, val) => sum + val, 0) ||
            0;
        }

        // 格式化数据
        let yearData = [...breakEvenData[indicator.key]];
        if (indicator.format === "percentage") {
          yearData = yearData.map((val) =>
            val !== 0 ? Number(val.toFixed(2)) + "%" : "",
          );
        } else {
          yearData = yearData.map((val) =>
            val !== 0 ? Number(val.toFixed(2)) : "",
          );
        }

        // 构建行数据
        const rowValues = [
          seq++,
          indicator.name,
          totalValue !== 0 ? Number(totalValue.toFixed(2)) : "",
          ...yearData,
        ];

        dataRow.values = rowValues;
        dataRow.font = {
          name: "Microsoft YaHei",
          size: 9, // 字体大小为9px
        };
      }
    });

    // 设置所有行的边框和对齐方式
    for (let rowIndex = 2; rowIndex < currentRow; rowIndex++) {
      const row = worksheet.getRow(rowIndex);
      const rowValues = row.values;
      for (let colIndex = 1; colIndex <= calculationPeriod + 3; colIndex++) {
        const cell = row.getCell(colIndex);
        cell.border = this.getDefaultBorder();

        // 序号列居中
        if (colIndex === 1) {
          cell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };
        }
        // 项目名称列左对齐
        else if (colIndex === 2) {
          cell.alignment = {
            vertical: "middle",
            horizontal: "left",
          };
        }
        // 其他列（数值）右对齐
        else {
          cell.alignment = {
            vertical: "middle",
            horizontal: "right",
          };
        }
      }
    }

    // 应用Excel样式（跳过表头样式，因为已经在上面设置了）
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出敏感性分析
  static exportSensitivityAnalysis(projectData, workbook) {
    const sensitivityData =
      projectData["不确定性分析"]?.["sensitivityAnalysis"] || {};
    const sensitivityTable = sensitivityData.sensitivityTable || [];

    // 创建工作表
    const worksheet = workbook.addWorksheet("敏感性分析");

    // 定义变化率列 - 从数据中动态获取所有唯一的变化率
    const changeRates = [
      ...new Set(sensitivityTable.map((item) => item.changeRate)),
    ].sort((a, b) => {
      // 将百分比字符串转换为数字进行排序
      const aVal = parseFloat(a);
      const bVal = parseFloat(b);
      return aVal - bVal;
    });

    // 定义变化因素行 - 从数据中动态获取所有唯一的变化因素
    const factors = [
      ...new Set(sensitivityTable.map((item) => item.factorName)),
    ];

    // 设置列宽
    const columns = [{ header: "变化因素\\变化率", width: 18 }];
    for (let i = 0; i < changeRates.length; i++) {
      columns.push({ header: changeRates[i], width: 12 });
    }
    worksheet.columns = columns;

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(65 + columns.length - 1); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "敏感性分析表";

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    const headerValues = ["变化因素\\变化率", ...changeRates];
    headerRow.values = headerValues;

    // 设置表头行样式 - 与数据行相同（无背景色、有边框、各列格式与数据行一样）
    headerRow.eachCell({ includeEmpty: true }, (cell, colIndex) => {
      cell.border = this.getDefaultBorder();
      cell.font = {
        name: "Microsoft YaHei",
        size: 9,
        bold: true,
      };

      // 设置对齐方式 - 与数据行相同
      if (colIndex === 1) {
        // 第一列（变化因素列）左对齐
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };
      } else {
        // 其他列（数值列）右对齐
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };
      }
    });

    // 构建数据矩阵
    const dataMatrix = {};
    factors.forEach((factor) => {
      dataMatrix[factor] = {};
      changeRates.forEach((rate) => {
        dataMatrix[factor][rate] = "";
      });
    });

    // 填充数据
    sensitivityTable.forEach((item) => {
      const factor = item.factorName;
      const rate = item.changeRate;
      const irr = item.irr;

      if (factors.includes(factor) && changeRates.includes(rate)) {
        dataMatrix[factor][rate] = irr;
      }
    });

    // 添加数据行
    factors.forEach((factor, rowIndex) => {
      const row = worksheet.getRow(rowIndex + 3);
      const rowValues = [factor];

      changeRates.forEach((rate) => {
        rowValues.push(dataMatrix[factor][rate]);
      });

      row.values = rowValues;
    });

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出项目参数表
  static exportProjectParams(projectData, workbook) {
    // 创建工作表
    const worksheet = workbook.addWorksheet("项目参数表");

    // 设置列宽
    worksheet.columns = [
      { width: 10 },
      { width: 30 },
      { width: 15 },
      { width: 15 },
    ];

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "项目参数表";

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    headerRow.values = ["序号", "名称", "指标", "单位"];
    // 确保表头文字居中
    headerRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
    });

    // 添加数据行
    const paramsData = this.getProjectParamsData(projectData);
    paramsData.forEach((rowData, index) => {
      const row = worksheet.getRow(index + 3);
      if (index === 0) {
        // 修改第1行数据，将指标和单位合并为一列
        row.values = [
          rowData[0],
          rowData[1],
          `${rowData[2]} ${rowData[3]}`,
          "",
        ]; // 合并指标和单位列

        // 合并第3列和第4列
        worksheet.mergeCells(`${index + 3}`, 3, `${index + 3}`, 4);
      } else {
        row.values = rowData;
      }

      // 设置数据行样式
      for (let j = 1; j <= 4; j++) {
        const cell = row.getCell(j);
        cell.border = ExcelStyles.getDefaultBorder();
        if (j === 1) {
          // 序号列居中
          cell.alignment = { vertical: "middle", horizontal: "center" };
        } else if (j === 2) {
          // 名称列左对齐
          cell.alignment = { vertical: "middle", horizontal: "left" };
        } else if (j === 3) {
          // 指标列居中
          cell.alignment = { vertical: "middle", horizontal: "center" };
        } else if (j === 4) {
          // 单位列居中
          cell.alignment = { vertical: "middle", horizontal: "center" };
        }
        cell.font = { name: "Microsoft YaHei", size: 9 };
      }
    });

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    // 重新设置指标列（第3列）和单位列（第4列）的对齐方式，确保不会被统一样式覆盖
    for (let rowIndex = 3; rowIndex <= paramsData.length + 2; rowIndex++) {
      const row = worksheet.getRow(rowIndex);

      // 设置指标列（第3列）居中对齐
      const indicatorCell = row.getCell(3);
      indicatorCell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };

      // 设置单位列（第4列）居中对齐
      const unitCell = row.getCell(4);
      unitCell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
    }

    return worksheet;
  }
  static exportUncertaintyAnalysis(projectData, workbook) {
    const uncertaintyData = projectData["不确定性分析"] || {};
    const sensitivityData = uncertaintyData["sensitivityAnalysis"] || {};
    const breakEvenData = uncertaintyData["breakEvenAnalysis"] || {};

    // 创建工作表
    const worksheet = workbook.addWorksheet("不确定性分析");

    // 设置列宽
    worksheet.columns = [
      { header: "项目", width: 18 },
      { header: "数值", width: 20 },
    ];

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "不确定性分析";

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    headerRow.values = ["项目", "数值"];

    // 添加数据行
    const dataRows = [
      ["敏感性分析"],
      ["- 基础内部收益率", sensitivityData.baseIRR || 0],
      ["盈亏平衡分析"],
      ["- 盈亏平衡产量", breakEvenData.breakEvenOutput || 0],
      ["- 盈亏平衡生产能力利用率（%）", breakEvenData.breakEvenCapacity || 0],
      ["- 盈亏平衡销售收入（万元）", breakEvenData.breakEvenRevenue || 0],
    ];

    dataRows.forEach((rowData, index) => {
      const row = worksheet.getRow(index + 3);
      row.values = rowData;
    });

    // 合并分类标题单元格
    worksheet.mergeCells("A3:B3"); // 敏感性分析
    worksheet.mergeCells("A5:B5"); // 盈亏平衡分析

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出敏感度系数和临界点分析表
  static exportSensitivityCoeffTable(projectData, workbook) {
    const uncertaintyData = projectData["不确定性分析"] || {};
    const sensitivityData = uncertaintyData["sensitivityAnalysis"] || {};
    const sensitivityTable = sensitivityData.sensitivityTable || [];

    // 创建工作表
    const worksheet = workbook.addWorksheet("敏感度系数和临界点分析表");

    // 设置列宽
    worksheet.columns = [
      { header: "序号", width: 8 },
      { header: "不确定因素", width: 18 },
      { header: "变化率(%)", width: 12 },
      { header: "内部收益率", width: 12 },
      { header: "敏感系数", width: 12 },
      { header: "临界点(%)", width: 12 },
      { header: "临界值", width: 25 },
    ];

    // 添加表格标题 - 合并范围与实际列数匹配
    const titleMergeEndColumn = String.fromCharCode(
      65 + worksheet.columns.length - 1,
    ); // 65是'A'的ASCII码
    worksheet.mergeCells(`A1:${titleMergeEndColumn}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "敏感度系数和临界点分析表";

    // 添加表头行
    const headerRow = worksheet.getRow(2);
    headerRow.values = [
      "序号",
      "不确定因素",
      "变化率(%)",
      "内部收益率",
      "敏感系数",
      "临界点(%)",
      "临界值",
    ];
    // 表头样式将由ExcelStyles统一设置

    // 添加数据行
    let currentRow = 3;

    // 首先设置所有行的数据，然后再进行合并操作
    // 第一步：设置所有行的基本数据
    sensitivityTable.forEach((item, index) => {
      const dataRow = worksheet.getRow(currentRow + index);

      // 设置行数据
      const rowValues = [
        item.seq || index + 1, // 保留原始序号格式
        item.isFirstRow ? item.factorName : "", // 只有第一行显示因素名
        item.changeRate || "",
        item.irr || "",
        item.sensitivityCoeff || "",
        item.isFirstRow ? item.criticalPoint || "" : "", // 只有第一行显示临界点
        item.isFirstRow && item.criticalValueName
          ? `${item.criticalValueName}\n数量临界值：${item.criticalValueQuantity}`
          : "", // 只有第一行显示临界值
      ];

      dataRow.values = rowValues;
      // 设置行的默认对齐方式为垂直居中
      dataRow.alignment = {
        vertical: "middle",
        wrapText: true,
      };

      // 对每个单元格单独设置样式和对齐方式
      dataRow.eachCell((cell, colNumber) => {
        // 设置字体大小为9px
        cell.font = {
          ...cell.font,
          size: 9,
        };

        // 应用数值格式化
        if (typeof cell.value === "number") {
          // 使用工具方法获取合适的数字格式
          const format = ExcelStyles.getNumberFormat(cell.value);
          if (format === "0.00%") {
            // 对于百分比数值，转换为百分比格式
            if (Math.abs(cell.value) < 0.00005) {
              cell.value = "";
            } else {
              cell.numFmt = "0.00%";
            }
          } else if (format === "#,##0.00") {
            // 对于普通数值
            if (Math.abs(cell.value) < 0.005) {
              cell.value = "";
            } else {
              cell.numFmt = "#,##0.00";
            }
          }
        }

        // 第1列（序号）和因素名列可以居中
        if (colNumber === 1 || colNumber === 2) {
          cell.alignment = {
            ...cell.alignment,
            horizontal: "center",
          };
        } else if (
          cell.value !== null &&
          cell.value !== undefined &&
          cell.value !== ""
        ) {
          // 其他有值的单元格右对齐（数字列）
          cell.alignment = {
            ...cell.alignment,
            horizontal: "right",
          };
        }
      });

      // 设置边框
      for (let colIndex = 1; colIndex <= rowValues.length; colIndex++) {
        const cell = dataRow.getCell(colIndex);
        cell.border = this.getDefaultBorder();
      }
    });

    // 第二步：处理跨行合并
    let rowIndex = currentRow;
    for (let i = 0; i < sensitivityTable.length; i++) {
      const item = sensitivityTable[i];

      if (item.isFirstRow && item.rowSpan > 1) {
        const startRow = rowIndex;
        const endRow = startRow + item.rowSpan - 1;

        // 1. 不确定因素列（B列）跨行合并
        worksheet.mergeCells(`B${startRow}:B${endRow}`);

        // 2. 临界点(%)列（F列）跨行合并
        worksheet.mergeCells(`F${startRow}:F${endRow}`);

        // 3. 临界值列（G列）跨行合并
        worksheet.mergeCells(`G${startRow}:G${endRow}`);

        // 设置合并后单元格的对齐方式
        const factorCell = worksheet.getCell(`B${startRow}`);
        const criticalPointCell = worksheet.getCell(`F${startRow}`);
        const criticalValueCell = worksheet.getCell(`G${startRow}`);

        factorCell.alignment = { vertical: "middle", horizontal: "center" };
        criticalPointCell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        criticalValueCell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };

        // 跳过已经合并的行
        i += item.rowSpan - 1;
        rowIndex += item.rowSpan;
      } else {
        rowIndex++;
      }
    }

    // 更新当前行位置
    currentRow = currentRow + sensitivityTable.length;

    // 设置样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }
  // 获取项目参数数据
  static getProjectParamsData(projectData) {
    const basicData = projectData["基础数据"] || {};
    const investmentData = projectData["总投资估算"]?.["总表"] || {};
    const fundingData = projectData["资金筹措及分年投资计划"] || {};
    const costData = projectData["成本费用"]?.["总成本费用"] || {};
    const revenueData = projectData["营业收入税金及利润"] || {};
    const profitData = revenueData["利润"] || {};
    const profitabilityData = projectData["盈利能力分析"] || {};
    const allInvestmentData = profitabilityData["全部投资现金流量"] || {};
    const capitalData = profitabilityData["资本金现金流量表"] || {};
    const breakEvenData = projectData["不确定性分析"]?.["盈亏平衡分析"] || {};

    // 获取流动资金相关数据
    const workingCapitalData = projectData["流动资金估算"] || {};
    // 获取折旧摊销相关数据
    const depreciationData = projectData["折旧与摊销"] || {};

    // 计算借款额度总和
    const debtData = fundingData["资金筹措"]?.["债务资金"] || {};
    const bankLoan = debtData["银行借款"]?.amount || 0;
    const bond = debtData["债券"]?.amount || 0;
    const lease = debtData["融资租赁"]?.amount || 0;
    const otherLoan = debtData["其他借款"]?.amount || 0;
    const totalLoan = bankLoan + bond + lease + otherLoan;

    // 计算销售利润率
    const revenueTotal = revenueData["营业收入及营业税金"]?.revenuetotal || 1;
    const preTaxSalesProfit =
      revenueTotal > 0 ? (profitData.total / revenueTotal) * 100 : 0;
    const afterTaxSalesProfit =
      revenueTotal > 0 ? (profitData.netProfit / revenueTotal) * 100 : 0;

    // 将还款资金来源代码转换为中文名称
    let repaymentSourceText = "利润、折旧、摊销";
    switch (profitData.repaymentSource) {
      case "netProfit":
        repaymentSourceText = "净利润";
        break;
      case "ebitda":
        repaymentSourceText = "息税折旧摊销前利润";
        break;
      case "profitBeforeTax":
        repaymentSourceText = "税前利润";
        break;
      default:
        repaymentSourceText = "净利润"; // 默认值
    }

    // 项目参数数据 - 按照用户要求的完整顺序排列
    const paramsData = [
      [
        "1",
        "项目名称",
        projectData.projectName || basicData.projectName || "未命名项目",
        "",
      ],
      ["2", "项目计算期", parseInt(basicData.calculationPeriod || 15), "年"],
      ["3", "项目建设期", parseInt(basicData.constructionPeriod || 1), "年"],
      ["4", "项目运营期", parseInt(basicData.operationPeriod || 14), "年"],
      ["5", "指标计算正常年", parseInt(basicData.normalYear || 1), ""],
      [
        "6",
        "总投资",
        parseFloat((investmentData.总投资?.合计 || 0).toFixed(2)),
        "万元",
      ],
      [
        "7",
        "建设投资",
        parseFloat((investmentData.建设投资?.合计 || 0).toFixed(2)),
        "万元",
      ],
      [
        "8",
        "流动资金",
        parseFloat((investmentData.流动资金?.合计 || 0).toFixed(2)),
        "万元",
      ],
      [
        "9",
        "建设期利息",
        parseFloat((investmentData.建设期利息?.合计 || 0).toFixed(2)),
        "万元",
      ],
      [
        "10",
        "自有资本金",
        parseFloat(
          (fundingData["资金筹措"]?.["项目资本金"]?.amount || 0).toFixed(2),
        ),
        "万元",
      ],
      [
        "11",
        "借款额度（所有借款品种额度相加）",
        parseFloat(totalLoan.toFixed(2)),
        "万元",
      ],
      ["12", "还款资金来源", repaymentSourceText, ""],
      [
        "13",
        "正常年盈亏平衡点",
        parseFloat(
          (
            breakEvenData.bepUtilizationRateByYear?.[
              basicData.normalYear - 1
            ] || 0
          ).toFixed(2),
        ) + "%",
        "",
      ],
      [
        "14",
        "正常年营业收入",
        parseFloat(
          revenueData["营业收入及营业税金"]?.revenueByYear?.[
            basicData.normalYear - 1 || 0
          ] || 0,
        ).toFixed(2),
        "万元",
      ],
      [
        "15",
        "总成本费用",
        parseFloat(
          (costData.year?.[basicData.normalYear - 1 || 0] || 0).toFixed(2),
        ),
        "万元",
      ],
      [
        "16",
        "正常年利润总额",
        parseFloat(
          (
            profitData.profitBeforeTaxByYear?.[basicData.normalYear - 1 || 0] ||
            0
          ).toFixed(2),
        ),
        "万元",
      ],
      [
        "17",
        "正常年净利润",
        parseFloat(
          (
            profitData.netProfitYear?.[basicData.normalYear - 1 || 0] || 0
          ).toFixed(2),
        ),
        "万元",
      ],
      [
        "18",
        "正常年销售利润率（利润总额）",
        parseFloat(profitData.profitMargins.salesProfitRate.toFixed(2)) + "%",
        "",
      ],
      [
        "19",
        "正常年销售利润率（净利润）",
        parseFloat(profitData.profitMargins.salesNetProfitRate).toFixed(2) +
          "%",
        "",
      ],
      [
        "20",
        "基准收益率(%)",
        parseFloat(basicData.discountRate || 10).toFixed(2) + "%",
        "",
      ],
      [
        "21",
        "全部投资内部收益率（税前）",
        parseFloat(allInvestmentData.irrBeforeTax || 0).toFixed(2) + "%",
        "",
      ],
      [
        "22",
        "全部投资内部收益率（税后）",
        parseFloat((allInvestmentData.irr || 0).toFixed(2)) + "%",
        "",
      ],
      [
        "23",
        "资本金内部收益率（税前）",
        parseFloat(capitalData.irrBeforeTax || 0).toFixed(2) + "%",
        "",
      ],
      [
        "24",
        "资本金内部收益率（税后）",
        parseFloat(capitalData.irr || 0).toFixed(2) + "%",
        "",
      ],
      [
        "25",
        "全部投资财务净现值（税前）",
        parseFloat(
          (allInvestmentData.discountedNetCashFlowTotalBeforeTax || 0).toFixed(
            2,
          ),
        ),
        "万元",
      ],
      [
        "26",
        "全部投资财务净现值（税后）",
        parseFloat(
          (allInvestmentData.discountedNetCashFlowTotal || 0).toFixed(2),
        ),
        "万元",
      ],
      [
        "27",
        "资本金财务净现值（税前）",
        parseFloat(
          (capitalData.discountedNetCashFlowBeforeTaxTotal || 0).toFixed(2),
        ),
        "万元",
      ],
      [
        "28",
        "资本金财务净现值（税后）",
        parseFloat((capitalData.discountedNetCashFlowTotal || 0).toFixed(2)),
        "万元",
      ],
      [
        "29",
        "全部投资静态投资回收期（税前）",
        parseFloat(allInvestmentData.paybackPeriodBeforeTax || 0).toFixed(2),
        "年",
      ],
      [
        "30",
        "全部投资静态投资回收期（税后）",
        parseFloat((allInvestmentData.paybackPeriod || 0).toFixed(2)),
        "年",
      ],
      [
        "31",
        "全部投资动态投资回收期（税前）",
        parseFloat(
          (allInvestmentData.discountedPaybackPeriodBeforeTax || 0).toFixed(2),
        ),
        "年",
      ],
      [
        "32",
        "全部投资动态投资回收期（税后）",
        parseFloat((allInvestmentData.discountedPaybackPeriod || 0).toFixed(2)),
        "年",
      ],
      [
        "33",
        "资本金静态投资回收期（税前）",
        parseFloat(capitalData.paybackPeriod || 0).toFixed(2),
        "年",
      ],
      [
        "34",
        "资本金静态投资回收期（税后）",
        parseFloat(capitalData.paybackPeriodBeforeTax || 0).toFixed(2),
        "年",
      ],
      [
        "35",
        "资本金动态投资回收期（税前）",
        parseFloat(
          (capitalData.discountedPaybackPeriodBeforeTax || 0).toFixed(2),
        ),
        "年",
      ],
      [
        "36",
        "资本金动态投资回收期（税后）",
        parseFloat((capitalData.discountedPaybackPeriod || 0).toFixed(2)),
        "年",
      ],
      [
        "37",
        "流动资金贷款比例",
        parseFloat((workingCapitalData.loanRatio || 0).toFixed(2)) + "%",
        "",
      ],
      [
        "38",
        "流动资金贷款利率",
        parseFloat((workingCapitalData.loanInterestRate || 0).toFixed(2)) + "%",
        "",
      ],
      [
        "39",
        "基本预备费率",
        parseFloat((investmentData.基本预备费率 || 5).toFixed(2)) + "%",
        "",
      ],
      [
        "40",
        "涨价预备费率",
        parseFloat((investmentData.涨价预备费率 || 0).toFixed(2)) + "%",
        "",
      ],
      [
        "41",
        "修理费率",
        parseFloat((projectData["成本费用"]?.repaircost || 0).toFixed(2)) + "%",
        "",
      ],
      [
        "42",
        "房屋建筑物折旧年限",
        parseInt(
          projectData["成本费用"]?.["折旧"]?.categories?.房屋及建构筑物
            ?.depreciationYears || 20,
        ),
        "年",
      ],
      [
        "43",
        "房屋建筑物残值率",
        parseFloat(
          (
            projectData["成本费用"]?.["折旧"]?.categories?.房屋及建构筑物
              ?.salvageRate || 5
          ).toFixed(2),
        ) + "%",
        "",
      ],
      [
        "44",
        "机械设备折旧年限",
        parseInt(
          projectData["成本费用"]?.["折旧"]?.categories?.设备
            ?.depreciationYears || 10,
        ),
        "年",
      ],
      [
        "45",
        "机械设备残值率",
        parseFloat(
          (
            projectData["成本费用"]?.["折旧"]?.categories?.设备?.salvageRate ||
            5
          ).toFixed(2),
        ) + "%",
        "",
      ],
      [
        "46",
        "无形资产摊销年限",
        parseInt(
          projectData["成本费用"]?.["摊销"]?.items?.无形资产
            ?.amortizationYears || 10,
        ),
        "年",
      ],
      [
        "47",
        "其他资产摊销年限",
        parseInt(
          projectData["成本费用"]?.["摊销"]?.items?.其他资产
            ?.amortizationYears || 5,
        ),
        "年",
      ],
      [
        "48",
        "城建税率",
        parseFloat(
          (revenueData["营业收入及营业税金"]?.UMCTax || 7).toFixed(2),
        ) + "%",
        "",
      ],
      [
        "49",
        "教育费附加税率",
        parseFloat(
          (revenueData["营业收入及营业税金"]?.Educationtax || 3).toFixed(2),
        ) + "%",
        "",
      ],
      [
        "50",
        "消费税率",
        parseFloat(
          (revenueData["营业收入及营业税金"]?.consumptiontax || 0).toFixed(2),
        ) + "%",
        "",
      ],
      [
        "51",
        "所得税率",
        parseFloat(
          (
            projectData["营业收入税金及利润"]?.["利润"]?.incomeTaxRate || 25
          ).toFixed(2),
        ) + "%",
        "",
      ],
      [
        "52",
        "法定盈余公积金率",
        parseFloat(
          (
            projectData["营业收入税金及利润"]?.["利润"]?.法定盈余公积金Rate || 0
          ).toFixed(2),
        ) + "%",
        "",
      ],
      [
        "53",
        "任意盈余公积金率",
        parseFloat(
          (
            projectData["营业收入税金及利润"]?.["利润"]?.任意盈余公积金Rate || 0
          ).toFixed(2),
        ) + "%",
        "",
      ],
      [
        "54",
        "优先股分配比例",
        parseFloat(
          (
            projectData["营业收入税金及利润"]?.["利润"]?.preferredStockRate || 0
          ).toFixed(2),
        ) + "%",
        "",
      ],
    ];

    return paramsData;
  }

  // 导出ECharts图表到Excel
  static async exportChartToExcel(
    chartInstance,
    workbook,
    sheetName,
    position = "A1",
    base64Data = null,
  ) {
    // 检查是否有Base64数据或图表实例
    if (!base64Data && !chartInstance) {
      console.warn("既没有Base64数据也没有图表实例，跳过图表导出");
      return null;
    }

    try {
      let chartBase64;

      // 如果提供了base64Data，直接使用它
      if (base64Data) {
        console.log("使用传入的Base64数据导出图表");
        chartBase64 = base64Data;
      } else {
        // 获取图表的Base64数据
        console.log("图表实例类型:", typeof chartInstance);
        console.log("图表实例方法:", Object.keys(chartInstance));

        // 检查图表实例是否有getDataURL方法
        if (typeof chartInstance.getDataURL !== "function") {
          console.error("图表实例没有getDataURL方法:", chartInstance);
          return null;
        }

        try {
          // 检查图表实例是否已经被销毁
          if (
            chartInstance._disposed ||
            !chartInstance._dom ||
            !chartInstance._zr
          ) {
            console.error("图表实例已经被销毁，无法获取Base64数据");
            return null;
          }

          // 确保图表容器有正确的尺寸
          if (
            !chartInstance._dom.offsetWidth ||
            !chartInstance._dom.offsetHeight
          ) {
            chartInstance._dom.style.width = "800px";
            chartInstance._dom.style.height = "600px"; // 增加高度，确保所有内容都能显示
            console.log("设置图表容器尺寸为800px×600px");
          }

          // 确保图表容器可见
          if (chartInstance._dom.style.display === "none") {
            chartInstance._dom.style.display = "block";
            console.log("将图表容器设置为可见");
          }

          // 调用resize方法确保图表重新渲染
          chartInstance.resize();

          // 等待图表渲染完成 - 增加等待时间
          await new Promise((resolve) => setTimeout(resolve, 300));

          // 设置图表的option确保所有元素都可见
          const currentOption = chartInstance.getOption();
          if (currentOption) {
            // 确保所有系列都可见
            if (currentOption.series) {
              currentOption.series.forEach((series) => {
                series.show = true;
                // 确保所有标注都可见
                if (series.markLine) {
                  series.markLine.show = true;
                }
                if (series.markPoint) {
                  series.markPoint.show = true;
                }
                if (series.label) {
                  series.label.show = true;
                }
              });
            }
            // 确保坐标轴可见
            if (currentOption.xAxis) {
              currentOption.xAxis.forEach((axis) => {
                axis.show = true;
                // 确保坐标轴标签可见
                if (axis.axisLabel) {
                  axis.axisLabel.show = true;
                }
                if (axis.axisName) {
                  axis.axisName.show = true;
                }
              });
            }
            if (currentOption.yAxis) {
              currentOption.yAxis.forEach((axis) => {
                axis.show = true;
                // 确保坐标轴标签可见
                if (axis.axisLabel) {
                  axis.axisLabel.show = true;
                }
                if (axis.axisName) {
                  axis.axisName.show = true;
                }
              });
            }
            // 确保图例可见
            if (currentOption.legend) {
              currentOption.legend.show = true;
            }
            // 确保标题可见
            if (currentOption.title) {
              currentOption.title.show = true;
            }
            // 应用更新后的配置
            chartInstance.setOption(currentOption, true);
            // 再次等待渲染完成 - 增加等待时间
            await new Promise((resolve) => setTimeout(resolve, 300));
          }

          // 再次调用resize确保所有元素都被正确布局
          chartInstance.resize();
          // 最后一次等待，确保所有元素都渲染完成
          await new Promise((resolve) => setTimeout(resolve, 200));

          chartBase64 = chartInstance.getDataURL({
            pixelRatio: 2, // 增加像素比以提高图片质量
            backgroundColor: "#fff",
            excludeComponents: ["toolbox"], // 排除不必要的组件
          });

          // 检查chartBase64是否有效
          if (!chartBase64) {
            console.error("获取到的Base64数据为空");
            return null;
          }

          console.log("获取到图表Base64数据长度:", chartBase64.length);

          // 检查是否获取到有效的Base64数据
          if (!chartBase64.startsWith("data:image/png;base64,")) {
            console.error("获取到的Base64数据无效:", chartBase64);
            return null;
          }
        } catch (dataURLError) {
          console.error("获取图表Base64数据失败:", dataURLError);
          return null;
        }
      }

      // 处理Base64数据，移除前缀
      const base64WithoutPrefix = chartBase64.replace(
        /^data:image\/png;base64,/,
        "",
      );
      console.log("处理后的Base64数据长度:", base64WithoutPrefix.length);

      // 将Base64转换为ArrayBuffer（浏览器环境兼容）
      const binaryString = atob(base64WithoutPrefix);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // 直接使用Uint8Array，ExcelJS 4.4.0支持Uint8Array

      // 获取或创建工作表
      let worksheet;
      if (workbook.worksheets.find((sheet) => sheet.name === sheetName)) {
        worksheet = workbook.getWorksheet(sheetName);
      } else {
        worksheet = workbook.addWorksheet(sheetName);
      }

      // 添加图片到工作簿
      console.log("开始添加图片到工作簿");
      try {
        // 使用ExcelJS 4.4.0完全支持的格式添加图片
        const imageId = workbook.addImage({
          base64: base64WithoutPrefix,
          extension: "png",
        });
        console.log("成功添加图片到工作簿");

        // 设置更灵活的图片位置和大小
        worksheet.addImage(imageId, {
          tl: { col: 0, row: 1 }, // 左上角：第0列第1行
          br: { col: 10, row: 35 }, // 右下角：第10列第35行，增加高度以适应更大的图表
          editAs: "oneCell",
        });
        console.log("成功在工作表中插入图片");
      } catch (imageError) {
        console.error("添加图片失败，将使用替代方案:", imageError);

        // 添加详细的图表信息作为替代
        const infoCells = [
          { cell: "A2", value: "图表信息", bold: true, merge: "E2" },
          {
            cell: "A4",
            value: "图表类型",
            value2:
              sheetName === "盈亏平衡分析图"
                ? "盈亏平衡分析图"
                : "敏感性分析图",
          },
          {
            cell: "A5",
            value: "图表描述",
            value2:
              sheetName === "盈亏平衡分析图"
                ? "展示项目的盈亏平衡点和盈利区间"
                : "展示各种因素对项目内部收益率的影响",
          },
          {
            cell: "A6",
            value: "查看说明",
            value2: "请在Web应用中查看完整的图表展示",
          },
          { cell: "A8", value: "图表数据", bold: true, merge: "E8" },
          { cell: "A10", value: "由于Excel导出限制，无法直接显示图表" },
        ];

        infoCells.forEach((item) => {
          const cell = worksheet.getCell(item.cell);
          cell.value = item.value;
          cell.font = { size: 11, bold: item.bold || false };
          cell.alignment = { horizontal: "left", vertical: "middle" };

          if (item.merge) {
            worksheet.mergeCells(`${item.cell}:${item.merge}`);
          }

          if (item.value2) {
            const valueCell = worksheet.getCell(
              item.cell.replace(/[0-9]+$/, (n) => parseInt(n) + 1),
            );
            valueCell.value = item.value2;
            valueCell.font = { size: 11 };
            valueCell.alignment = { horizontal: "left", vertical: "middle" };
          }
        });
      }

      return worksheet;
    } catch (error) {
      console.error("导出图表到Excel失败:", error);
      return null;
    }
  }

  // 导出盈亏平衡分析图到Excel
  static async exportBreakevenChart(
    projectData,
    workbook,
    chartInstance,
    base64Data = null,
  ) {
    const sheetName = "盈亏平衡分析图";

    // 创建工作表
    const worksheet = workbook.addWorksheet(sheetName);

    // 添加图表标题
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "盈亏平衡分析图";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    // 合并标题行
    worksheet.mergeCells("A1:E1");

    // 如果有图表实例或base64Data，导出图表
    console.log("盈亏平衡分析图表实例:", chartInstance);
    console.log(
      "盈亏平衡分析图表base64Data:",
      base64Data ? "已提供" : "未提供",
    );
    if (chartInstance || base64Data) {
      const result = await this.exportChartToExcel(
        chartInstance,
        workbook,
        sheetName,
        "A2:E20",
        base64Data,
      );
      console.log("盈亏平衡分析图表导出结果:", result);
      if (!result) {
        console.warn("盈亏平衡分析图表导出失败");
        // 添加失败提示
        const errorCell = worksheet.getCell("A3");
        errorCell.value = "图表导出失败，无法显示图片";
        errorCell.font = { size: 11, color: { argb: "FFFF0000" } };
      }
    } else {
      console.warn("盈亏平衡分析图表实例为空，将显示图表数据");
      // 添加图表数据作为替代
      const bepData = projectData?.不确定性分析?.盈亏平衡分析 || {};
      const normalYearIndex = bepData.normalYearIndex || 0;
      const bepUtilizationRate = bepData.breakevenUtilizationRate || 0;
      const bepRevenue = bepData.breakevenRevenue || 0;

      // 添加盈亏平衡分析数据
      worksheet.getCell("A3").value = "盈亏平衡分析数据";
      worksheet.getCell("A3").font = { size: 11, bold: true };
      worksheet.mergeCells("A3:E3");

      worksheet.getCell("A5").value = "分析年份";
      worksheet.getCell("B5").value = "正常年份";

      worksheet.getCell("A6").value = "盈亏平衡收入";
      worksheet.getCell("B6").value = bepRevenue.toFixed(2);
      worksheet.getCell("C6").value = "万元";

      worksheet.getCell("A7").value = "盈亏平衡生产能力利用率";
      worksheet.getCell("B7").value = (bepUtilizationRate * 100).toFixed(2);
      worksheet.getCell("C7").value = "%";

      // 设置数据格式
      for (let i = 5; i <= 7; i++) {
        worksheet.getCell(`A${i}`).font = { size: 11, bold: true };
        worksheet.getCell(`B${i}`).font = { size: 11 };
        worksheet.getCell(`C${i}`).font = { size: 11 };
      }
    }

    // 应用Excel样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }

  // 导出敏感性分析图到Excel
  static async exportSensitivityChart(
    projectData,
    workbook,
    chartInstance,
    base64Data = null,
  ) {
    const sheetName = "敏感性分析图";

    // 创建工作表
    const worksheet = workbook.addWorksheet(sheetName);

    // 添加图表标题
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "敏感性分析图";
    titleCell.font = {
      name: "Microsoft YaHei",
      size: 12,
      bold: true,
      color: { argb: "FF2C3E50" },
    };
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    // 合并标题行
    worksheet.mergeCells("A1:E1");

    // 如果有图表实例或base64Data，导出图表
    console.log("敏感性分析图表实例:", chartInstance);
    console.log("敏感性分析图表base64Data:", base64Data ? "已提供" : "未提供");
    if (chartInstance || base64Data) {
      const result = await this.exportChartToExcel(
        chartInstance,
        workbook,
        sheetName,
        "A2:E20",
        base64Data,
      );
      console.log("敏感性分析图表导出结果:", result);
      if (!result) {
        console.warn("敏感性分析图表导出失败");
        // 添加失败提示
        const errorCell = worksheet.getCell("A3");
        errorCell.value = "图表导出失败，无法显示图片";
        errorCell.font = { size: 11, color: { argb: "FFFF0000" } };
      }
    } else {
      console.warn("敏感性分析图表实例为空，将显示图表数据");
      // 添加敏感性分析数据作为替代
      const sensitivityData = projectData?.不确定性分析?.敏感性分析 || {};
      const sensitivityResults = sensitivityData?.敏感性分析结果 || {};

      // 添加敏感性分析数据标题
      worksheet.getCell("A3").value = "敏感性分析数据";
      worksheet.getCell("A3").font = { size: 11, bold: true };
      worksheet.mergeCells("A3:E3");

      // 添加表头
      worksheet.getCell("A5").value = "指标名称";
      worksheet.getCell("B5").value = "变化率";
      worksheet.getCell("C5").value = "内部收益率";
      worksheet.getCell("D5").value = "敏感度系数";

      // 设置表头样式
      for (let col = 0; col < 4; col++) {
        worksheet.getCell(`${String.fromCharCode(65 + col)}5`).font = {
          size: 11,
          bold: true,
        };
      }

      // 添加数据行
      let row = 6;
      for (const factor in sensitivityResults) {
        if (sensitivityResults.hasOwnProperty(factor)) {
          const factorData = sensitivityResults[factor];
          worksheet.getCell(`A${row}`).value = factorData.指标名称 || factor;

          // 添加变化率和对应的内部收益率
          const changeRates = factorData.变化率 || [];
          const irrs = factorData.收益率 || [];

          // 只显示前5个数据点
          const maxDataPoints = Math.min(changeRates.length, 5);
          for (let i = 0; i < maxDataPoints; i++) {
            worksheet.getCell(`B${row + i}`).value = changeRates[i] + "%";
            worksheet.getCell(`C${row + i}`).value = irrs[i] + "%";
          }

          row += maxDataPoints;
        }
      }

      // 如果没有数据，显示提示
      if (row === 6) {
        worksheet.getCell("A6").value = "暂无敏感性分析数据";
        worksheet.getCell("A6").font = { size: 11, color: { argb: "#999999" } };
        worksheet.mergeCells("A6:E6");
      }
    }

    // 应用Excel样式
    ExcelStyles.setExcelJSStyle(worksheet);

    return worksheet;
  }
}

export default ExcelData;
